// ---------------------------------------------------------------------------
// The room lobby. The busiest screen in the game.
//
// Five decisions here are the design rather than the styling:
//
//   1. One list holds teams and solo players. A free-for-all room is the common
//      case and it must not read as a teams feature somebody forgot to use,
//      which is why there is no "no teams yet" caption and no empty container
//      waiting to be filled.
//   2. **Nothing about the room is kept locally.** Teams, readiness and the
//      round plan all render straight from server state. Every one of them was
//      local once, and every one of them produced the same bug: the player who
//      pressed the button saw one room and everybody else saw another.
//   3. A non-host sees the settings as static text, not as disabled inputs. A
//      disabled stepper invites a player to press it and then says no; text says
//      what the room is set to and leaves it there.
//   4. Host powers render only for the host. The server refuses them from anyone
//      else, so showing them would buy nothing except a button that always
//      fails, which is a lie.
//   5. **Most settings are behind `Advanced`, on every screen size.** A default
//      plan used to open with seven sliders, two toggles, two steppers and a
//      segmented control all at once, and the collapse that existed was
//      `lg:hidden` — so the widest screen, where the wall is longest, folded
//      nothing. What stays out is what decides a game: the three play clocks,
//      whether words are checked, who may talk, who can find the room, and the
//      plan itself. **How many can play is no longer among them** — it is an
//      advanced cap that defaults to no cap at all, because the control a room
//      actually wants for its door is the lock at the top of the seat list.
//   6. **The door is the lock, not a number.** `roomLocked` seats the people
//      who are here and sends everybody after them to the bench, which is what
//      a room of friends with an audience watching actually wants, and it is
//      the one control a new host most needs explained — so the explanation is
//      in its own description slot and a two-line note under it. See
//      `lobby/RoomDoor.tsx`, and `lobby/copy.ts` for the wording, which the
//      mid-game safety panel draws from the same place.
//
// **The header carries marks, not a row of text buttons.** It was the room
// code, `Copy link`, `Hide code`, a `Playing | Spectating` segment and the
// connection meter, which is five controls and about 380px of chrome above a
// screen whose actual content is the seat list. Copy and mask are icon buttons
// with real accessible names; the seat choice moved onto your own row, where
// the thing it changes is; and what is left beside them is the one fact a
// player joining a room wants confirmed — whether this room is private.
//
// **Private and public is one state with two controls on it.** The chip in the
// header and the segment under Settings both read `settings.visibility` and
// both write it, so neither can be the stale one. The chip is there because a
// room's privacy is not a setting you go looking for, it is something you
// should be able to see at a glance while reading the code out loud.
//
// **Nothing here is keyed on which round types the plan contains.** Every timer
// is always on screen. It used to hide the cards for types the plan had no
// round of, on the reasoning that a conundrum timer in a room with no conundrum
// is a setting to read past — but a settings column that re-flows every time a
// chip is added to the plan above it moves the control under the host's cursor,
// and `Advanced` now does the decluttering that hiding was doing. A patch still
// carries only the fields it touches, so a timer nobody moves is a value nobody
// writes.
//
// **Your name and your colour are one sheet, opened from your own card.** See
// `lobby/EditPlayerSheet.tsx`: they were a dialog behind a pencil and a
// disclosure further down the column, answering the same question in two
// places. The note beside `saveEdit` below says why the *room* reports a
// suffixed name rather than the form.
//
// Colour belongs to the team while teams are on. Every player drawn on this
// screen goes through `seatColor`, never `player.color`, or the room ends up
// with two colour systems saying different things about the same person — and
// `seatColor` returns **null** in a room past `COLORED_SEATS`, which every
// caller here draws rather than substituting a grey for.
//
// **Teams are on when there is a team.** There was a `teamsEnabled` setting
// beside `state.teams` and the two could disagree; the array is the one answer
// and `teamed` is how this screen asks it.
//
// The chat is not here. The room shell owns it, and owns the site header, so
// this screen reserves no column for either.
// ---------------------------------------------------------------------------

import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactElement, ReactNode } from 'react';
import {
  LARGE_ROOM_FROM,
  MAX_PLAYERS_HARD_CAP,
  MAX_SPECTATORS_CAP,
  REASONING_AVAILABLE,
  REVEAL_PACE_DEFAULT,
  REVEAL_PACE_MAX,
  REVEAL_PACE_MIN,
  ROUND_SEQUENCE_PRESETS,
  TEAM_COLORS,
} from '@countdown/shared';
import type {
  BotDifficulty,
  GameSettings,
  PublicPlayer,
  RoomLock,
  RoundType,
} from '@countdown/shared';
import {
  Button,
  ConnectionStatus,
  IconButton,
  PlayerChip,
  ROUND_CHIP_LETTER,
  RoundSequenceSummary,
  RoundTypeChip,
  Segmented,
  Slider,
  StopSlider,
  Toggle,
} from '../../components/primitives/index.js';
import {
  AlertIcon,
  BotIcon,
  CheckIcon,
  CircleIcon,
  CopyIcon,
  EyeIcon,
  EyeOffIcon,
  GlobeIcon,
  Icon,
  LockIcon,
  PencilIcon,
  PlusIcon,
  ShieldCheckIcon,
} from '../../components/primitives/Icon.js';
import { useIsDesktop } from '../../hooks/useMediaQuery.js';
import { useSiteConfig } from '../../hooks/useSiteConfig.js';
import {
  formatSeatCost,
  listNames,
  modelName,
  roundLabel,
  seatCostLabel,
  seatCostPerGame,
  shareUrl,
  suggestedClockSeconds,
  usualLatencyLabel,
} from '../../lib/format.js';
import { membersOfTeam, seatedPlayers, spectators } from '../../lib/playerLookup.js';
import { useGameStore } from '../../store/gameStore.js';
import { usePlayerStore } from '../../store/playerStore.js';
import {
  BOT_DIFFICULTIES,
  DIFFICULTY_LABEL,
  HostRowMenu,
} from './lobby/HostRowMenu.js';
import { EditPlayerSheet } from './lobby/EditPlayerSheet.js';
import { RoomDoor } from './lobby/RoomDoor.js';
import { SafetyPanel } from './lobby/SafetyPanel.js';
import { actionBarRef } from './lobby/actionBar.js';
import { usePointerDrag } from './lobby/usePointerDrag.js';
import {
  AGGREGATE_HINT,
  AGGREGATE_LABEL,
  AGGREGATE_OPTIONS,
  AUDIENCE_AGGREGATION_HINT,
  AUDIENCE_AGGREGATION_LABEL,
  AUDIENCE_AGGREGATION_OPTIONS,
  AUDIENCE_HINT,
  AUDIENCE_LABEL,
  AUDIENCE_WINS_HINT,
  AUDIENCE_WINS_LABEL,
  BUZZER_HINT,
  BUZZER_LABEL,
  CHAT_HINT,
  CHAT_LABEL,
  CHAT_OPTIONS,

  PERMISSIONS_HINT,
  PERMISSIONS_LABEL,
  LIMITS_HINT,
  PLAYER_LIMIT_LABEL,
  AUDIENCE_LIMIT_LABEL,
  SELF_SELECT_HINT,
  SELF_SELECT_LABEL,
  ROOM_PRESETS,
  VISIBILITY_HINT,
  VISIBILITY_LABEL,
} from './lobby/copy.js';
import {
  Badge,
  CardRule,
  LimitRow,
  ReadOnlyRow,
  Reveal,
  SectionLabel,
  SettingRow,
  SettingsCard,
  SettingsGroup,
  StaticValue,
  TimerField,
} from './lobby/fields.js';
import type { RoomScreenProps } from './types.js';

// The bounds the server enforces in `sanitizeSettings`. Module-private there, so
// restated here: a stepper must never offer a value the server would clamp.
const MAX_PLAYERS_MIN = 2;
const MAX_SPECTATORS_MIN = 0;

// The buzz window is a different kind of quantity from a round clock and gets a
// different range. It is the seconds a buzzing player has to say the word, ten
// on the show, and nothing about it wants five minutes. On the 5-to-300 track it
// shared with the round timers, every value anyone would ever pick lived in the
// first 8% of the rail. The server still accepts 5 to 300, so a shorter track
// can never offer a value it would refuse.
const BUZZ_MIN_SECONDS = 5;
const BUZZ_MAX_SECONDS = 30;

/** One copy of each piece of wording, so the host's control and the static row
 *  everybody else gets cannot drift into describing the same setting
 *  differently.
 *
 *  Two of the three carried a second sentence and neither earned it: one said
 *  a pause stops the clock, which is what a pause is, and the other said a fast
 *  model wins a race it had already said the model would enter. What is left is
 *  the one hint that states a rule its switch does not — which of a player's
 *  words gets scored. */
const WORD_CHECK_LABEL = 'Let players try as many words as they like';
const WORD_CHECK_HINT = 'The longest valid one is their answer.';
const BUZZ_MODE_LABEL = 'AI opponents buzz as soon as they have an answer';
const REAL_WORDS_LABEL = 'AI opponents skip their own made-up words';
const REAL_WORDS_HINT =
  'A model offers a few answers. On, it plays the best one that is really a word.';
const PAUSE_LABEL = 'Pause rounds when a player disconnects';

/**
 * The one thing this toggle has to say, and it is not what the toggle does.
 *
 * Host rulings cost the room its record — no stats, no leaderboard, no game
 * page — and that is decided by the *setting* rather than by whether anybody
 * ever rules on a word, because every narrower rule leaves a host having a bad
 * game something worth rigging. So the price is on the switch, before it goes
 * on, in the description slot the switch already has.
 */
const RULINGS_LABEL = 'Host can allow or reject a word';
/**
 * What it costs, said before it is pressed.
 *
 * **It no longer says "nothing about it is recorded", because that is no longer
 * true.** The game is written and gets its page; what it loses is its place in
 * everybody's statistics. The old sentence would have had a host expecting to
 * lose the scoreboard and the words, which is a worse thing than what happens
 * and would put them off a setting that is fine for a casual game.
 */
const RULINGS_HINT = 'For a casual game.';
const RULINGS_WARNING =
  'This game will still get its own page, and will count towards nobody’s stats.';

/**
 * The reveal pace slider's readout.
 *
 * **The number is not on screen and must not be.** `revealPacePercent` is a
 * multiplier on how long each answer holds, so a bigger number is a slower
 * reveal — and "150%" beside the word pace reads as faster to about half the
 * people who see it. What is drawn is what changes: the hold, and which way.
 */
const REVEAL_PACE_STEP = 10;

const REVEAL_PACE_MARKS = (value: number): string => {
  if (value === REVEAL_PACE_DEFAULT) return 'normal';
  const ratio = value > REVEAL_PACE_DEFAULT ? value / 100 : 100 / value;
  const rounded = Number(ratio.toFixed(1));
  return `${rounded}× ${value > REVEAL_PACE_DEFAULT ? 'longer' : 'shorter'}`;
};

const REVEAL_PACE_SPOKEN = (value: number): string =>
  value === REVEAL_PACE_DEFAULT
    ? 'the normal pace'
    : `each answer holds ${REVEAL_PACE_MARKS(value)}`;

/**
 * Wording for a preset whose shipped label is wrong on screen.
 *
 * `Half hour` described a nine-round plan by how long it takes rather than by
 * what it is, next to `Short` and `Long`, which describe themselves — so the row
 * read as two sizes and a duration. **This belongs in `ROUND_SEQUENCE_PRESETS`
 * in `packages/shared` and should be deleted the moment it lands there**; it is
 * here only because that package was being edited by somebody else in the same
 * pass. Keyed by preset id, so a rename over there makes this a no-op rather
 * than a contradiction.
 */
const PRESET_LABEL: Record<string, string> = { classic: 'Medium' };

/**
 * Seats the model preset leaves for people.
 *
 * **This is the whole of Isaac's worry about the preset**, and it is why the
 * button raises the room's own ceiling rather than filling it. Pressing "one of
 * every model" in an 8-seat room with one human in it wants nine seats, so
 * something has to give — and a host who ends up with a room no friend can join
 * has been handed a worse room than the one they pressed it in.
 *
 * Two, not one, because a room is a place people arrive at in pairs as often as
 * singly, and the ceiling it buys is free: `maxPlayers` is a limit on joining
 * and nothing else, so an unfilled seat costs the room nothing at all.
 *
 * It is applied twice, deliberately — once when working out how high the
 * ceiling has to go, and again when working out how many models fit under it.
 * The second covers a room whose limit is already at `MAX_PLAYERS_HARD_CAP`,
 * where the ceiling cannot be raised any further and the only way to leave a
 * seat free is to seat fewer models. That is now a validator bound of five
 * thousand rather than a palette-shaped sixteen, so it is a guard rather than a
 * case anybody meets — and a room with no limit at all, which is the default,
 * never touches any of this. See `presetFits`.
 */
const SEATS_KEPT_FREE = 2;

/**
 * Which kind of opponent the add panel is about to seat.
 *
 * **This was one control listing every difficulty plus `AI`, and five
 * difficulties is what ended it.** A four-way segment already did not fit on a
 * 319px card beside a button; six options is a row of unreadable slivers, and it
 * was never a flat choice anyway — AI is not a fourth difficulty, and a control
 * that lists it as one has to keep explaining that picking it throws the
 * difficulty away.
 *
 * So the kind is a two-option segment and the difficulty is a slider under it,
 * shown only while the kind is `bot`. The two questions are asked in the order
 * they are answered, and the second one disappears when it does not apply rather
 * than sitting there meaning nothing.
 */
type OpponentKind = 'bot' | 'ai';

/**
 * Which of the three targets on your own card opened the edit sheet.
 *
 * It decides one thing and it is not cosmetic: whether the name field takes
 * focus, and therefore whether a phone throws its keyboard over the colour grid
 * somebody just tapped a colour dot to reach. See `EditPlayerSheet`.
 */
type EditOrigin = 'name' | 'colour' | 'card';

/**
 * The pause between the last player readying up and the game starting itself.
 *
 * Long enough to take back — anybody un-readying cancels it — and short enough
 * that a room where everyone has already said yes is not waiting on a host to
 * press a button they have no reason to withhold.
 */
const AUTO_START_SECONDS = 5;

/** Remembered per device, not per room: somebody who streams streams every game. */
const CODE_HIDDEN_KEY = 'countdown-hide-room-code';

function readCodeHidden(): boolean {
  try {
    return window.localStorage.getItem(CODE_HIDDEN_KEY) === '1';
  } catch {
    // Private browsing, or storage switched off. The code shows, which is the
    // state the room was in before this control existed.
    return false;
  }
}

// --- The round plan builder ------------------------------------------------

/**
 * Geometry of the builder well, in pixels, because the drag has to do
 * arithmetic on it.
 *
 * The well carries no column gap of its own. Each round sits in a cell one gap
 * wider than the chip, and the drop indicator is a cell of the same width that
 * opens from zero. Both transition, so at every instant of a drag the cell
 * closing and the indicator opening add up to the same width: the plan cannot
 * reflow under the pointer, and nothing below the well moves.
 */
const CHIP_WIDTH = 44;
const CHIP_GAP = 6;
const CELL_WIDTH = CHIP_WIDTH + CHIP_GAP;

/**
 * What the gap that opens under a dragged round is filled with, per type.
 *
 * It was `blue-400` on `blue-700/45` whatever was being carried, which is the
 * letters chip's own colouring — so dragging a conundrum opened a letters-
 * shaped hole and the one thing the host is looking at said the wrong thing
 * about the thing they are holding. Each entry is the fill its own chip wears
 * in `RoundTypeChip`, at a dashed edge, so the gap is unmistakably the round in
 * the hand rather than a slot of its own.
 */
const DROP_SLOT_TINT: Record<RoundType, string> = {
  letters: 'border-blue-400 bg-blue-700/45',
  numbers: 'border-line-strong bg-raised',
  conundrum: 'border-accent bg-accent/15',
};

/** Movement before a press counts as a drag rather than as a tap to remove. */
const DRAG_THRESHOLD = 6;

// --- dragging a player into a team ----------------------------------------

/**
 * How long a finger has to rest on a seat row before it is carrying it.
 *
 * **The seat list is the thing that scrolls this screen**, so a drag that
 * started on movement would take the page's own gesture away and the lobby
 * would stop scrolling wherever a player happened to be. A hold has no such
 * clash: nothing else on this screen means "press and wait".
 *
 * 350ms, and the two ends of the range are both real. Much under 300 and an
 * ordinary tap on your own card — which opens the edit sheet — starts turning
 * into a drag on a slow thumb. Much over 400 and the row feels stuck: the
 * gesture has no feedback until it fires, so the wait *is* the whole of the
 * interaction until then. iOS's own long press is 500ms and is famously slow to
 * anyone who did not start it deliberately. Movement of more than
 * `DRAG_THRESHOLD` during the wait abandons the press and lets the list scroll,
 * so the cost of guessing slightly high is nothing at all.
 *
 * A mouse skips it entirely — see `usePointerDrag` — because a cursor has no
 * competing scroll gesture and holding still for a third of a second to move a
 * row on a desktop would be a worse version of what the round chips already do.
 */
const PLAYER_DRAG_HOLD_MS = 350;

/**
 * The unteamed group's id as a drop zone.
 *
 * Teams are the other zones and they use their own ids, so this has to be
 * something the server can never issue. Team ids are uuids.
 */
const UNTEAMED = 'unteamed';

/** A seat row in flight. `zone` is null while the pointer is over no target. */
interface PlayerDragState {
  id: string;
  zone: string | null;
  x: number;
  y: number;
  /** The row's own height, frozen at the pick-up, so the gap it leaves matches it. */
  height: number;
}

/**
 * Which drop zone is under the pointer, or null for none.
 *
 * `elementFromPoint` is the exact answer and it is immune to the list reflowing
 * as the preview opens — unlike the round drag's frozen grid, which is frozen
 * because a 44px chip's neighbours move under the finger and oscillate. A zone
 * here is a whole card: the only thing that happens to the one under the
 * pointer is that it gets taller *around* the pointer, so live measurement
 * cannot flicker and does not need freezing.
 *
 * jsdom implements no layout and therefore no `elementFromPoint`, so the event's
 * own target is the fallback. That is not a test-only branch with no meaning in
 * a browser: a pointer event's target is the element under the pointer, so the
 * two agree wherever both exist.
 */
function zoneUnder(event: PointerEvent): string | null {
  const under =
    typeof document.elementFromPoint === 'function'
      ? document.elementFromPoint(event.clientX, event.clientY)
      : null;
  const node = under ?? event.target;
  if (!(node instanceof Element)) return null;
  return node.closest('[data-drop-zone]')?.getAttribute('data-drop-zone') ?? null;
}

/**
 * The five seconds between the last player readying up and the game starting.
 *
 * Its own component so that mounting it *is* starting the countdown and
 * unmounting it *is* cancelling one: the seconds live in state that dies with
 * the element, so a countdown taken back and started again cannot come back
 * holding the number it stopped on.
 *
 * A deadline rather than a counter, for the same reason every clock in this
 * game is one. A decrementing counter drifts, and a background tab the browser
 * throttles would arrive at zero long after the five seconds were up.
 */
function AutoStart({ onFire }: { onFire: () => void }): ReactElement {
  const [left, setLeft] = useState(AUTO_START_SECONDS);

  useEffect(() => {
    const deadline = Date.now() + AUTO_START_SECONDS * 1_000;
    const timer = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1_000));
      setLeft(remaining);
      if (remaining > 0) return;
      clearInterval(timer);
      onFire();
    }, 250);
    return () => clearInterval(timer);
  }, [onFire]);

  return <>Starting in {left}</>;
}

interface DragState {
  /** Index in the plan of the round being carried. */
  from: number;
  /** Where it lands, as an index in the plan with `from` already taken out. */
  to: number;
  /** Pointer position, for the piece that follows the finger. */
  x: number;
  y: number;
}

/**
 * Where a round dropped at `x, y` lands, given the chip centres as they were
 * when the drag began.
 *
 * Exported because it is the whole of the drag that can be wrong: everything
 * else is a width transition. The answer is an index in the plan with `from`
 * already taken out, which is what `moveRound` wants.
 */
export function insertionIndex(
  centres: readonly { x: number; y: number }[],
  x: number,
  y: number,
  from: number,
): number {
  if (centres.length === 0) return from;

  // Row distance counts for more than column distance, so a pointer on the
  // second row never binds to a chip above it that happens to be closer.
  let best = centres.length;
  let bestDistance = Number.POSITIVE_INFINITY;
  centres.forEach((centre, index) => {
    const distance = Math.hypot(x - centre.x, (y - centre.y) * 3);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = x < centre.x ? index : index + 1;
    }
  });

  return best > from ? best - 1 : best;
}

// --- The screen -----------------------------------------------------------

export function LobbyScreen({ state, send }: RoomScreenProps): ReactElement {
  const quality = useGameStore((store) => store.quality);
  const rememberColor = usePlayerStore((store) => store.setPreferredColor);
  const rememberName = usePlayerStore((store) => store.setDisplayName);
  const isDesktop = useIsDesktop();

  const [editing, setEditing] = useState<EditOrigin | null>(null);
  const [copied, setCopied] = useState(false);
  const [codeHidden, setCodeHidden] = useState(readCodeHidden);
  const [advanced, setAdvanced] = useState(false);
  // The mid-game controls, opened from the header. In the lobby it is a shortcut
  // to settings that are also in the column below; the reason it exists is the
  // phase where that column is not on screen at all.
  const [safetyOpen, setSafetyOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  // Shut to begin with. The total is on the button that opens it, so the fact
  // most rooms want is already on screen and the breakdown is for the room that
  // wants to know which seat is spending it.
  const [costOpen, setCostOpen] = useState(false);
  const [newOpponentKind, setNewOpponentKind] = useState<OpponentKind>('bot');
  const [newOpponentDifficulty, setNewOpponentDifficulty] = useState<BotDifficulty>('medium');
  // Null until the host picks one, so the server's default is what an untouched
  // panel seats and this browser does not pin a model the catalog has dropped.
  const [newOpponentModel, setNewOpponentModel] = useState<string | null>(null);
  // Was a `useState` behind the `Thinking` switch. The switch is gone with
  // `REASONING_AVAILABLE` and this is what it always sent while off; the message
  // still carries the field, so the wiring is whole and the engine is what
  // actually decides — it stores false whatever arrives. Put the state back with
  // the switch, not before.
  const newOpponentReasoning = REASONING_AVAILABLE;
  const [drag, setDrag] = useState<DragState | null>(null);
  const [playerDrag, setPlayerDrag] = useState<PlayerDragState | null>(null);
  // The player whose host menu is open, and there is only ever one of them.
  // Held here rather than in each row so that opening one closes the last
  // without the two rows having to know about each other.
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const { config, loading: configLoading } = useSiteConfig();
  // The server derives this from the seat, and it is the only thing that may be
  // read for it. Asking Clerk in the browser instead was the bug where a host
  // who was demonstrably signed in was told to sign in.
  const signedIn = state.youAreSignedIn;
  const llm = config?.llm;

  /**
   * Why the AI option cannot be taken right now, or null when it can.
   *
   * One computation for both controls — the add panel's segment and the host
   * menu on every bot's row — so the two can never disagree about whether this
   * host may seat a language model, or about which sentence explains it.
   *
   * `null` for the whole thing, rather than a reason, when this server has AI
   * switched off: there is then no option to grey out and nothing to unlock, so
   * neither control offers one at all.
   */
  const aiSeating: { defaultModel: string; blockedReason: string | null } | null = llm?.enabled
    ? {
        defaultModel: llm.defaultModel,
        blockedReason: !signedIn
          ? 'Sign in to play a language model.'
          : llm.budgetExhausted
            ? "This month's AI budget is spent."
            : null,
      }
    : null;
  const aiModel = newOpponentModel ?? aiSeating?.defaultModel ?? null;

  /**
   * Whether the add panel offers an AI opponent at all.
   *
   * Absent while `/api/config` is still out, as well as when this server has AI
   * switched off: there is then nothing to unlock, so an option offering it
   * would be a lie rather than a locked door. Blocked is a different thing and
   * is still shown — greyed, with the reason under it.
   */
  const aiOffered = aiSeating !== null && !configLoading;
  /**
   * The kind the panel is actually set to, which is not always the kind the host
   * last pressed: a config that stops offering AI — or one that has not arrived
   * yet — puts the panel back on `bot` rather than leaving it pointed at a
   * choice that is no longer on screen. Derived rather than corrected in an
   * effect, which is a lint error here and a frame late besides.
   */
  const opponentKind: OpponentKind = aiOffered ? newOpponentKind : 'bot';

  const host = state.youAreHost;
  const settings = state.settings;
  const isPrivate = settings.visibility === 'private';
  const seated = seatedPlayers(state);
  const watching = spectators(state);
  const you = state.players.find((player) => player.id === state.youId) ?? null;
  const youAreSeated = you?.role === 'player';
  const plan = settings.roundSequence;
  const teams = state.teams;

  /**
   * Whether teams are on, which is a question with one answer and it is the
   * teams array.
   *
   * There was a `settings.teamsEnabled` beside it and the two could disagree —
   * a room with three teams and the flag off, or the flag on and nothing to
   * join. It is gone, and every reader asks the list instead.
   */
  const teamed = teams.length > 0;

  /**
   * What to draw a player in, or **null in a room with no colours in it**.
   *
   * The two rules, in the order they apply. Teams own colour outright, so while
   * teams are on a player wears their side's colour and not their own — two
   * colour systems competing for the same swatch is what made a teams game
   * unreadable. And past `COLORED_SEATS` nobody has a colour at all:
   * `colorForSeat` returns null for every seat in the room, not for the ones
   * past sixteen, so null here is a real answer and every caller draws it rather
   * than substituting a grey. A grey is itself a colour and reads as an
   * identity; above the line the room is told apart by name.
   *
   * It is local rather than `displayColorOf`, because that one still asks the
   * deleted flag and lives in a file this screen does not own.
   */
  const seatColor = (player: PublicPlayer): string | null =>
    teams.find((team) => team.id === player.teamId)?.color ?? player.color;

  /** Whether anybody in this room has a colour. See `colorForSeat`. */
  const roomHasColors = state.players.some((player) => player.color !== null);

  /**
   * Whether a player who is not the host may open a team, close an empty one, or
   * change their own colour. Off by default; see `playerPermissions`.
   *
   * The host is not covered by it — they are the person it is a permission
   * FROM — so every use is `host || permitted`.
   */
  const permitted = host || settings.playerPermissions;
  /** Whether a player may put themselves on a team. On by default, unlike above. */
  const canPickTeam = host || settings.allowTeamSelfSelect;

  // --- what the AI seats cost, and whether the clocks suit them --------------
  //
  // All of this is arithmetic on the catalogue `/api/config` already ships to
  // every client. There is no request here and there must not be one: the same
  // payload the model picker reads carries the prices, the token counts and the
  // latency, so a second endpoint would be a slower copy of a fact the browser
  // is holding.
  //
  // **Both panels are drawn for everybody in the room, not only the host**, and
  // that reverses nothing — it follows decision 3 at the top of this file. A
  // non-host already sees every setting as static text, because what a room is
  // set to is not a host power. A model that will not answer inside the clock
  // is the same kind of fact, and it is the one that explains an opponent
  // sitting out three rounds in a row; the only lever a guest has is asking the
  // host in chat, and nobody asks about something they cannot see. The wording
  // is therefore one string for both, like `WORD_CHECK_LABEL` above, rather
  // than an instruction for the host and a translation for everyone else.
  const catalogue = llm?.models ?? [];

  /**
   * Every AI seat, paired with what the catalogue says about the model it is
   * playing.
   *
   * One entry per SEAT and not per model. A room may deliberately seat the same
   * model twice — `db/subject.ts` is built around exactly that — and two seats
   * on one model are two players, two boards and two bills.
   *
   * `model` is null for a seat this catalogue cannot describe: a model retired
   * while a room sat in the lobby, or a client holding a config from before the
   * deploy. It is priced at nothing and says so, rather than being dropped from
   * a list of who is playing.
   */
  const aiSeats = seated
    .filter((player) => player.botKind === 'llm')
    .map((player) => ({
      player,
      model: catalogue.find((entry) => entry.id === player.botModel) ?? null,
    }));

  /** The seats above with a price against each, for the plan this room is on. */
  const seatCosts = aiSeats.map((seat) => ({
    ...seat,
    usd: seat.model === null ? null : seatCostPerGame(seat.model, plan.length),
  }));
  const totalCostUsd = seatCosts.reduce((sum, seat) => sum + (seat.usd ?? 0), 0);
  /** Whether anything in the table could be priced at all. */
  const anySeatPriced = seatCosts.some((seat) => seat.usd !== null);

  /**
   * The models in this room that want a longer clock than the room is set to,
   * with what each of them wants.
   *
   * **One row per MODEL rather than per seat**, which is the one place the two
   * differ from the cost breakdown above: a bill is per seat and doubles when a
   * model is seated twice, while "this model is slow" is the same sentence
   * whichever seat it is about, and saying it twice reads as two problems.
   *
   * Ordered by what they want, longest first. One clock covers every model
   * under it, so the first row is the only number a host has to act on, and the
   * rows below it explain themselves once that one is taken.
   */
  const clocksInPlan = (
    [
      ['letters', settings.lettersPlaySeconds],
      ['numbers', settings.numbersPlaySeconds],
      ['conundrum', settings.conundrumSeconds],
    ] as const
  ).filter(([type]) => plan.includes(type));

  const slowModels = catalogue
    .filter((model) => aiSeats.some((seat) => seat.model?.id === model.id))
    .map((model) => {
      const wants = suggestedClockSeconds(model);
      const spoken = usualLatencyLabel(model);
      if (wants === null || spoken === null) return null;
      // Only the phases actually in the round plan. Warning about the conundrum
      // clock in a room with no conundrum sends a host to a setting that will
      // never come up.
      const short = clocksInPlan.filter(([, value]) => value < wants);
      if (short.length === 0) return null;
      return { model, wants, spoken, rounds: short.map(([type]) => roundLabel(type)) };
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry !== null)
    .sort((a, b) => b.wants - a.wants);

  // --- your own name ---
  //
  // A rename can no longer be refused for being a duplicate: the server suffixes
  // the person who asked, so `Isaac` next to an `Isaac` comes back as
  // `Isaac (2)`. That is the one outcome worth saying out loud, because from
  // inside the sheet it looks identical to a rename that did nothing — the name
  // you typed is not the name you got.
  //
  // So the room is what reports it, not the form. What this browser asked for is
  // held until the *next change* to our own name lands, and only a change is
  // read: comparing against the name we already had would fire on the render
  // between the send and the broadcast, when the answer has not arrived yet.
  const yourName = you?.displayName ?? null;
  const askedName = useRef<string | null>(null);
  const knownName = useRef<string | null>(yourName);

  useEffect(() => {
    if (yourName === knownName.current) return;
    const asked = askedName.current;
    knownName.current = yourName;
    askedName.current = null;
    if (asked === null || yourName === null) return;
    const fold = (name: string) => name.replace(/\s+/g, ' ').trim().toLowerCase();
    if (fold(yourName) === fold(asked)) return;
    useGameStore.getState().pushToast(`Somebody was already ${asked}, so you are ${yourName}.`);
  }, [yourName]);

  /**
   * One save for both halves of the sheet, sending only what changed.
   *
   * Two messages rather than one, because they are two actions the server has
   * always had. A colour that did not move must not be re-sent: `set-color`
   * broadcasts the whole room to everybody in it.
   */
  /**
   * The colour, on the press.
   *
   * `EditPlayerSheet` has no Cancel any more, so there is nothing to hold this
   * for — see the note at the top of that file, which reverses an earlier
   * decision deliberately.
   */
  const pickColor = (color: string) => {
    if (you === null || color === you.color) return;
    send({ type: 'set-color', color });
    // The only colour picker on the site, so it is also the only thing that can
    // record what this player likes. The join sends `preferredColor`, so the
    // next room opens on it instead of on whatever the server had spare.
    rememberColor(color);
  };

  /**
   * The name, once, when the sheet is done with.
   *
   * Deliberately NOT on every keystroke, which is what "save as they type"
   * would mean here: a rename runs `uniqueDisplayName` against the whole room,
   * broadcasts to everybody in it and writes a system line, so per-letter saving
   * would put `Isaac is now Isa` through the room once per letter. The sheet
   * hands this a cleaned name or null, and null is both "unchanged" and "not a
   * name".
   */
  const renameTo = (name: string) => {
    if (name === yourName) return;
    askedName.current = name;
    // What they asked to be called, not what the room settled on: the next room
    // they join should open on the name they chose and suffix it again if it
    // has to.
    rememberName(name);
    send({ type: 'rename', displayName: name });
  };

  /** In your own row and nowhere else, because it edits you and only you. */
  const editPencil = (
    <IconButton
      size="sm"
      variant="ghost"
      label="Change your name and colour"
      title="Change your name and colour"
      onClick={() => setEditing('name')}
    >
      <Icon icon={PencilIcon} size={15} />
    </IconButton>
  );

  /**
   * Playing or spectating, on your own card rather than in the header.
   *
   * It was a `Playing | Spectating` segment up beside the room code, which is
   * about 150px of header spent on a choice almost nobody changes, a long way
   * from the row it is about. Moving it onto the row it is about still stands.
   *
   * **It is a word now, not a mark**, which reverses the other half of that
   * decision. The eye and the person glyph were meant to say what pressing them
   * does — an eye to step out and watch, a person to sit back down — in the way
   * the code mask beside them does. It did not carry: a sighted player saw an
   * eye and could not tell it was about to take their seat away, and this is
   * not a control that can afford to be guessed at. Pressing it puts you out of
   * the game. `aria-label` had said `Watch instead of playing` all along, so a
   * screen reader was the only user it was ever clear to, which is a strange
   * way round.
   *
   * The alternative on the table was moving it somewhere with more room. It is
   * rejected: moving it costs everybody who has already found it, and buys
   * something a label buys too.
   *
   * **The wording is the row's own idiom** — a lowercase mono tag, uppercased in
   * the shipping, the same as `ready`, `waiting` and `disconnected` a few
   * pixels away, and the same shape as the `join` / `leave` / `delete` buttons
   * on a team header below. `aria-label` keeps the full sentence and stays the
   * accessible name; it contains the visible word, so a voice-control user
   * saying "watch" still hits it.
   *
   * **The width was measured rather than argued about**, in the browser at
   * 375px, on a host's own row with a `guest` tag and a crown — the widest this
   * row gets. `watch` at `sm` takes the button from 44px to 68.2, and the name
   * does NOT pay all 24.2 of that: the chip was carrying about 15.6px of slack,
   * because the name is `truncate` and stops growing at its own content width.
   * The ceiling on a name before it truncates goes from 98.4px to 89.8 — about
   * eleven characters of Archivo semibold at 16px down to about ten — and a
   * name shorter than that is not touched at all (a 30px name measures 30 both
   * ways).
   *
   * Eight pixels off the longest name this row can hold is affordable
   * precisely because the control is on ONE row and it is yours: the name it
   * can shorten is the name its reader already knows, and every other seat in
   * the roster keeps its full width. The two arrangements that did not survive
   * the measurement are the glyph AND the word (83.2px, 15 more than the word
   * alone, for a mark that had already failed to say anything) and `spectate`
   * instead of `watch` (94.7px, which is most of a name).
   */
  /**
   * Changing sides, offered at the heading of the side you are not on.
   *
   * **It used to be a word on your own row and that is the wrong place for it.**
   * A row in the roster says who somebody is; it is a list entry, and the one
   * belonging to you had two controls bolted onto it that no other row had — so
   * the action "join the game" was hidden inside a list of people rather than
   * sitting at the top of the list you would be joining. Anybody looking for it
   * had to find their own name first.
   *
   * At the heading it reads as what it is: `players 3` with a Join on the right
   * of it, `audience 12` with a Watch on the right of it. Only ever one of them
   * is on screen, because only one of them is a move you can make.
   */
  const door: RoomLock = settings.roomLock ?? 'open';

  // **The door is why this is not always a button.** A locked room puts arrivals
  // in the audience and the way back is the host seating them, so offering this
  // would be a press the server refuses — decision 4 at the top of this file.
  const joinPlayersControl = youAreSeated ? null : host || door === 'open' ? (
    <Button
      size="sm"
      variant="outline"
      aria-label="Join the game"
      onClick={() => send({ type: 'take-seat' })}
    >
      Join
    </Button>
  ) : (
    <span
      data-testid="seat-locked"
      title="The host has locked the room. They can still give you a place."
      className="px-2 font-mono text-[11px] font-semibold tracking-[0.08em] text-ink-dim uppercase"
    >
      locked
    </span>
  );

  // Stepping OUT is never gated: the door is about who comes in, not about
  // holding people in their chairs.
  const watchControl = youAreSeated ? (
    <Button
      size="sm"
      variant="ghost"
      aria-label="Watch instead of playing"
      onClick={() => send({ type: 'become-spectator' })}
    >
      Watch
    </Button>
  ) : null;

  const patch = (change: Partial<GameSettings>) => send({ type: 'update-settings', settings: change });
  const pushSequence = (next: RoundType[]) => patch({ roundSequence: next });

  // A room whose buzz window was set past the top of this track before it was
  // narrowed keeps it, and the track grows to hold it rather than the control
  // showing a value nobody chose. It snaps back the moment the host moves it
  // down into the sane range.
  const buzzMax = Math.max(BUZZ_MAX_SECONDS, settings.conundrumBuzzAnswerSeconds);

  const moveRound = useCallback(
    (from: number, to: number) => {
      if (to < 0 || to >= plan.length || from === to) return;
      const next = [...plan];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      send({ type: 'update-settings', settings: { roundSequence: next } });
    },
    [plan, send],
  );

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2_000);
    return () => clearTimeout(timer);
  }, [copied]);

  // The link is built whether or not the code is on screen, so Copy link still
  // works for a host who has masked it. Masking is about what a camera sees.
  const url = shareUrl(state.roomCode);
  const copyLink = () => {
    void navigator.clipboard?.writeText(url);
    setCopied(true);
  };

  /**
   * Whether the code is masked on THIS screen. Local, per device, and that is
   * now the whole of it.
   *
   * **There was a room SETTING that hid the code from everybody else and it has
   * been deleted.** It masked a string in the UI while the code sat in the
   * address bar of every person it was hidden from, so it protected nothing from
   * anybody who could read their own URL — a curtain in front of an open window.
   * Worse, it read as access control, which is the one thing it was not: the
   * door is `roomLock`, and that is where somebody who does not want people
   * joining should be sent.
   *
   * What survives is the useful half, which was always the local one: a host
   * with a camera pointed at their screen masks the code on their own display
   * while Copy link goes on working. That is a fact about this device and it
   * belongs in `localStorage`, not in the room's settings.
   */
  const codeMasked = codeHidden;

  const hideCode = (next: boolean) => {
    setCodeHidden(next);
    try {
      window.localStorage.setItem(CODE_HIDDEN_KEY, next ? '1' : '0');
    } catch {
      // Nothing to do: the choice holds for this page and is forgotten on the
      // next one, which is better than failing the press.
    }
  };

  /**
   * Seat whatever the one control is set to.
   *
   * **An AI opponent is seated at `medium`, always.** `botDifficulty` is not
   * dead weight on an LLM — it is what a bot *chooser* calls its tiles and
   * picks its large count with, whichever kind of bot it is — but it governs
   * nothing about the answer a model gives, so a difficulty on this panel would
   * be a strength dial wired to the one thing it does not control. The panel
   * hides the difficulty slider for exactly that reason, and medium is the
   * neutral caller. The old panel sent whichever difficulty happened to be
   * selected when `Add AI` was pressed, which was the same value arrived at by
   * accident.
   */
  const addOpponent = () => {
    if (opponentKind !== 'ai') {
      send({ type: 'add-bot', difficulty: newOpponentDifficulty });
      return;
    }
    if (aiModel === null) return;
    send({
      type: 'add-bot',
      difficulty: 'medium',
      botKind: 'llm',
      model: aiModel,
      reasoning: newOpponentReasoning,
    });
  };

  // --- the one list ---
  const solo = seated.filter((player) => !teams.some((team) => team.id === player.teamId));
  /**
   * Seats left, or **null when the room has no limit** — which is the default
   * and the ordinary case.
   *
   * Null is not zero and not infinity: it is "there is no number to report", and
   * every reader below has to answer it rather than substituting one. A room
   * with no cap has nothing to say about how full it is, so the row that used to
   * say it says nothing instead of `Infinity open seats`.
   */
  const openSeats = settings.maxPlayers === null ? null : Math.max(0, settings.maxPlayers - seated.length);
  const canMakeTeams = permitted && (host || youAreSeated);
  // Who the opener is for. Not a disabled button in either case — see the note
  // on the open-seats row. `addOpen` is not reset when this goes false, so a
  // host who fills the room and then raises `maxPlayers` finds the panel where
  // they left it rather than having to open it twice.
  const canAdd = host && openSeats !== 0;
  const seatsCopy =
    openSeats === null
      ? null
      : openSeats === 0
        ? 'Every seat taken'
        : openSeats === 1
          ? 'One open seat'
          : `${openSeats} open seats`;

  // --- one of every model ---------------------------------------------------
  //
  // **The constraint is the ROOM's own setting, and in the ordinary room there
  // is not one.** `settings.maxPlayers` defaults to null — no limit — so a
  // default room seats the whole catalogue and moves nothing. This whole block
  // is about the room whose host has gone into Advanced and set a number, which
  // was every room when the default was eight against a ceiling of sixteen.
  //
  // Where there is a limit, the preset raises it — which is a number the host
  // could have moved themselves — and leaves `SEATS_KEPT_FREE` under it for
  // people. Two alternatives were rejected: seating only what fits under the
  // existing ceiling (a button called "one of every model" that seats five of
  // eight is a button that does not do what it says), and filling the room to
  // its ceiling exactly (which is the room-nobody-can-join Isaac was worried
  // about).
  //
  // Nothing here is a new message. Each seating is the `add-bot` this screen
  // has always sent, and the ceiling is the `update-settings` the stepper
  // sends. The burst is bounded by the catalogue at eight, plus one settings
  // patch, against a socket limiter of thirty messages a second.

  /** Models with no seat in the room yet, cheapest per game first. */
  const unseatedModels = catalogue.filter(
    (model) => !aiSeats.some((seat) => seat.model?.id === model.id),
  );

  /**
   * Where `maxPlayers` has to be for the whole catalogue to sit down with room
   * left over — or null in a room with no limit, where there is nothing to move.
   */
  const presetCeiling =
    settings.maxPlayers === null
      ? null
      : Math.min(
          MAX_PLAYERS_HARD_CAP,
          Math.max(settings.maxPlayers, seated.length + unseatedModels.length + SEATS_KEPT_FREE),
        );

  /**
   * How many models actually get a seat.
   *
   * All of them, unless a room's own limit is already at `MAX_PLAYERS_HARD_CAP`
   * and there is no ceiling left to raise — a validator bound of five thousand
   * rather than a real ceiling, so the short branch is a guard rather than
   * something a host meets.
   */
  const presetFits =
    presetCeiling === null
      ? unseatedModels.length
      : Math.max(
          0,
          Math.min(unseatedModels.length, presetCeiling - seated.length - SEATS_KEPT_FREE),
        );

  /**
   * Whether to offer the preset at all.
   *
   * The same gate as the AI option beside it — a server with AI switched off, a
   * host who is signed out, a spent budget — plus one of its own: a catalogue
   * with a single model in it has no preset to offer, because "one of every
   * model" would be the Add AI button with a longer label.
   */
  const presetOffered =
    aiSeating !== null && !configLoading && aiSeating.blockedReason === null && catalogue.length > 1;

  /**
   * Seat the catalogue, and say what the press did that the roster cannot.
   *
   * The order is the catalogue's, which the server publishes cheapest per game
   * first, so a room that cannot take all of them loses the expensive end. That
   * is the safer half to lose and it is also the half a host can add back by
   * hand from this same panel.
   *
   * Names cannot collide: the server names an AI seat from `shortName`, no two
   * of which match (`models.test.ts` holds that), and this seats each model at
   * most once. `uniqueDisplayName` would suffix a clash anyway, exactly as it
   * does for two people called Isaac.
   */
  const seatEveryModel = () => {
    if (presetFits === 0) return;
    // Only when the room has a limit and it is in the way. A room with none —
    // the default — sends no patch at all, because there is nothing to raise.
    const raising =
      settings.maxPlayers !== null && presetCeiling !== null && presetCeiling > settings.maxPlayers;
    // First, so every `add-bot` behind it is measured against the new ceiling.
    // Messages leave one socket in order and the server applies them in order.
    if (raising) patch({ maxPlayers: presetCeiling });

    const seating = unseatedModels.slice(0, presetFits);
    for (const model of seating) {
      // Medium for the same reason `addOpponent` sends medium: a difficulty on
      // an AI seat is a strength dial wired to the one thing it cannot move.
      send({
        type: 'add-bot',
        difficulty: 'medium',
        botKind: 'llm',
        model: model.id,
        reasoning: newOpponentReasoning,
      });
    }

    // Only the parts the room cannot see for itself. Eight new rows appearing
    // in the seat list is not news; the ceiling moving and a model being left
    // out are both things that happened without being asked for.
    const notes: string[] = [];
    if (raising) notes.push(`The room holds ${presetCeiling} now.`);
    const missed = unseatedModels.slice(presetFits);
    if (missed.length > 0) {
      notes.push(`No seat for ${listNames(missed.map((model) => model.label))}.`);
    }
    if (notes.length > 0) useGameStore.getState().pushToast(notes.join(' '));
  };

  const createTeam = () => {
    // The team is the room's, not this browser's: it comes back in `state.teams`
    // or it does not exist.
    //
    // **And that is the whole of it now.** There was a `teamsEnabled: true`
    // patch behind this press, because teams were a setting as well as a list,
    // and the two could disagree. Teams are on when there is a team, so making
    // one is the only act there is.
    send({ type: 'create-team' });
  };

  // --- ready, and the countdown it starts ---
  //
  // Ready is a signal rather than a gate. `startGame` has never read `readyIds`
  // and the host's button has never been disabled by it, so a second button
  // called "Start without waiting" dispatched the identical action and was only
  // ever a second name for the first one. What readiness decides is this: once
  // every seated player has said yes, the game starts itself in five seconds.
  //
  // Every bot counts as ready (the engine adds them, see `readyIdsFor`), so in
  // a solo room the human's own press is the last one and starts the game.
  //
  // The countdown is derived from server state rather than stored anywhere, so
  // every client shows it and anybody un-readying cancels it everywhere. Only
  // the host dispatches at zero, because the server refuses a start from anyone
  // else.
  const ready = new Set(state.readyIds);
  const youReady = ready.has(state.youId);
  const readyCount = seated.filter((player) => ready.has(player.id)).length;
  const everyoneReady = seated.length > 0 && readyCount === seated.length;

  // A room with nobody human in a seat never starts itself, and this is not a
  // refinement of "everyone is ready" — it is the case where that sentence is
  // vacuous. Every bot is ready from the moment it is seated, so a roster of
  // nothing but bots is unanimously ready before anybody has done anything.
  //
  // Reported by Isaac, who took the spectator seat in his own room: the game
  // started without him, played itself out, returned to the lobby, and started
  // again. Nothing was going to stop it, because the condition it was waiting
  // for had been satisfied since the first bot arrived, and returning to the
  // lobby satisfied it again.
  //
  // Watching a table of bots play is a thing somebody might genuinely want, so
  // the answer is not to forbid it — it is that a room of bots is started on
  // purpose, by a person, rather than by a countdown nobody armed.
  const someoneHumanIsPlaying = seated.some((player) => !player.isBot);
  const autoStartArmed = everyoneReady && someoneHumanIsPlaying;

  // Refreshed every render so the timer below can fire without listing `send`
  // as a dependency: `send` is rebuilt whenever the connection object changes,
  // and a countdown that restarted on a socket event would never reach zero.
  const startNow = useRef<() => void>(() => {});
  useEffect(() => {
    startNow.current = () => {
      if (host) send({ type: 'start-game', actionId: crypto.randomUUID() });
    };
  });

  // Stable, so mounting the countdown does not restart it on every render.
  const fireStart = useCallback(() => startNow.current(), []);

  /**
   * What a seat's readiness says, or nothing at all.
   *
   * **A player who is not connected has no readiness to report**, and this
   * returns null for them. It reverses an arrangement where a dropped player's
   * row said two things at once: `PlayerChip` drew its `disconnected` tag and
   * this drew `waiting` beside it, because neither knew the other existed.
   *
   * That was worse than untidy. `waiting` means "has not pressed ready yet",
   * which is a statement about somebody's intent — and the truth about a player
   * who has dropped is that nobody is at the keyboard to have one. The row was
   * manufacturing a fact about a person out of their absence, and putting it in
   * the column a host reads down to decide whether to start.
   *
   * **Neither component learns about the other**, which is why the rule is here
   * rather than at the call site or in the chip. Readiness is the lobby's own
   * idea — the chip has never heard of it — and "an absent player has no
   * readiness" is a fact about readiness, not about what the chip happens to
   * draw. The chip goes on owning the connection state and saying
   * `disconnected` by itself, exactly as it did; the only thing it changed on
   * its side is where that tag sits, so it lands in the column this one has
   * given up.
   *
   * The `w-[7ch]` below is untouched and still doing its original job. It is
   * the width of the wider of the two words that swap *in place* on a row
   * somebody is looking at, so readying up does not resize the row. Dropping
   * out is not that swap: it fades the whole row and puts an alert-coloured tag
   * in it, and nobody toggles it back and forth from the chair.
   */
  const readyMarker = (player: PublicPlayer) => {
    if (!player.connected) return null;
    return (
      <span
        // Widest of the two words, so a player readying up does not resize the
        // row they are sitting in.
        className={`inline-block w-[7ch] text-right font-mono text-[11px] font-semibold tracking-[0.08em] uppercase transition-colors duration-200 ease-out ${
          ready.has(player.id) ? 'text-good' : 'text-ink-dim'
        }`}
        data-ready={ready.has(player.id) || undefined}
      >
        {ready.has(player.id) ? 'ready' : 'waiting'}
      </span>
    );
  };

  // --- dragging a seat into a team ---
  //
  // **No new engine action, and two different ones on purpose.** `assign-team`
  // is the host moving somebody; `join-team` is a player moving themselves. The
  // permission model is exactly what it was before this gesture existed, which
  // it would not be if every drop sent `assign-team` — that would hand a
  // non-host a way to move other people, from a screen where the server would
  // refuse it and the row would silently spring back.
  //
  // **Nothing moves locally.** The drop sends a message and the roster arrives
  // from the server, like every other fact on this screen (decision 2 at the top
  // of this file). A drag that ends over nothing therefore needs no undo: there
  // was never anything to undo, and clearing the drag state is the whole of it.
  //
  // **The keyboard has not lost anything.** Every team control that was here is
  // still here — `join` and `leave` on each team's header, `Move to …` and
  // `Play on their own` in the host's row menu — because a gesture no keyboard
  // can perform must not be the only way into a team. This is a faster path to
  // things that already had one.
  const playerDragRef = useRef<PlayerDragState | null>(null);
  const playerRowRefs = useRef(new Map<string, HTMLElement>());
  const dropHeight = useRef(0);

  const setPlayerDragState = (next: PlayerDragState | null) => {
    playerDragRef.current = next;
    setPlayerDrag(next);
  };

  /**
   * Who may pick a row up.
   *
   * Yours always, because `join-team` is yours to send. Anyone else's only as
   * host. And none of it at all until there is a team to drop into: with no
   * teams there is nowhere for a row to go, and a gesture that can only fail is
   * worse than no gesture.
   */
  const canDragPlayer = (player: PublicPlayer): boolean =>
    teamed && (player.id === state.youId ? canPickTeam : host);

  /**
   * What a player would be wearing if they landed in `zone`.
   *
   * Null all the way through in a room past `COLORED_SEATS`, where the preview
   * is drawn without a colour rather than in a stand-in for one.
   */
  const colorInZone = (player: PublicPlayer, zone: string | null): string | null => {
    if (zone === null) return seatColor(player);
    if (zone === UNTEAMED) return player.color;
    return teams.find((team) => team.id === zone)?.color ?? player.color;
  };

  const dropPlayer = (playerId: string, zone: string | null) => {
    // Released over nothing at all. The row was never moved, so there is
    // nothing to put back.
    if (zone === null) return;
    const dropped = state.players.find((entry) => entry.id === playerId);
    if (dropped === undefined) return;
    const teamId = zone === UNTEAMED ? null : zone;
    // Picked up and put down again where it started.
    if (dropped.teamId === teamId) return;
    if (playerId === state.youId) send({ type: 'join-team', teamId });
    else send({ type: 'assign-team', playerId, teamId });
  };

  const playerDragGesture = usePointerDrag<string>({
    threshold: DRAG_THRESHOLD,
    holdMs: PLAYER_DRAG_HOLD_MS,
    onStart: (id) => {
      // Frozen at the pick-up, like the round drag's grid: the gap that opens
      // in the destination is the row that is coming, so it is that row's size.
      const rect = playerRowRefs.current.get(id)?.getBoundingClientRect();
      dropHeight.current = rect === undefined || rect.height === 0 ? 0 : Math.round(rect.height);
    },
    onMove: (id, point) => {
      setPlayerDragState({
        id,
        zone: zoneUnder(point.event),
        x: point.x,
        y: point.y,
        height: dropHeight.current,
      });
    },
    onEnd: (id, dragged) => {
      const carried = playerDragRef.current;
      setPlayerDragState(null);
      if (!dragged || carried === null) return;
      dropPlayer(id, carried.zone);
    },
  });

  const registerPlayerRow = (id: string) => (node: HTMLDivElement | null) => {
    if (node === null) playerRowRefs.current.delete(id);
    else playerRowRefs.current.set(id, node);
  };

  /**
   * The gap that opens where a carried row will land, in the colour it will
   * land wearing.
   *
   * The round sequence does exactly this with a dashed cell tinted to the round
   * in the hand, and this is the same idea with the one rule teams add: while
   * teams are on a player wears their side's colour, so the preview inside a
   * team is that team's and the preview back among the solo players is the
   * player's own. An opaque `color-mix` rather than a wash, because a team card
   * is already tinted and stacking two washes falls off the bottom of what the
   * palette was solved for (CLAUDE.md).
   */
  const playerDropSlot = (color: string | null) => (
    <div
      key="player-drop-slot"
      aria-hidden="true"
      data-testid="player-drop-slot"
      data-drop-color={color ?? undefined}
      style={{
        height: playerDrag !== null && playerDrag.height > 0 ? playerDrag.height : undefined,
        // No colour in the room, so no colour in the preview. `line-strong` on
        // `raised` is the neutral slot the round plan already opens when it has
        // nothing to tint with, and both are opaque — a wash inside a tinted
        // team card composites past what the palette was solved for.
        ...(color === null
          ? {}
          : { borderColor: color, background: `color-mix(in oklab, ${color} 20%, var(--c-surface))` }),
      }}
      className={`min-h-14 rounded-tile border-2 border-dashed ${
        color === null ? 'border-line-strong bg-raised' : ''
      }`}
    />
  );

  /** The row in flight, or null. */
  const draggedPlayer =
    playerDrag === null
      ? null
      : (state.players.find((entry) => entry.id === playerDrag.id) ?? null);

  /** Whether a carried row is currently aimed at this zone, and is not already in it. */
  const previewIn = (zone: string): boolean => {
    if (playerDrag === null || playerDrag.zone !== zone || draggedPlayer === null) return false;
    return (draggedPlayer.teamId ?? UNTEAMED) !== zone;
  };

  /**
   * One seat.
   *
   * Your own row is the whole edit target: the pencil, the colour dot and the
   * card itself all open the same sheet, and which of them was pressed is
   * carried through so a phone does not raise its keyboard over the colour grid
   * somebody was aiming at. The dot is found by `data-player-dot`, which the
   * chip already draws, rather than by a second dot rendered on top of it.
   *
   * **Every control is outside the chip, at the end of the row, and every row
   * ends in at least 44px of something.** Two things were wrong when they were
   * inside it, and both were measured on the widest phone this is played on
   * rather than argued about:
   *
   *   - A 44px button inside a 26px chip makes that one row 18px taller than
   *     every other row in the list. Your own row was the tall one.
   *   - A 309px card carrying a dot, a name, an identity slot, a pencil, a seat
   *     toggle, a crown and a ready marker has 7px left over for the name. Out
   *     of the chip, the same row leaves it about ninety. (Measured again at
   *     375px while the seat toggle was being labelled: the card is 343 now,
   *     and a name on your own row may run to 98.4px with the toggle a 44px
   *     square and 89.8 with it a word.)
   *
   * **Your own row has no ready marker**, which is where most of that width
   * came from. It is the one row whose readiness is already stated, in the
   * green Ready button an inch below it, and a `waiting` beside your own name
   * while that button sits there unpressed says the same thing twice.
   */
  const playerRow = (player: PublicPlayer, nested: boolean) => {
    const isYou = player.id === state.youId;
    const draggable = canDragPlayer(player);
    const carrying = playerDrag?.id === player.id;
    return (
      <div
        key={player.id}
        ref={registerPlayerRow(player.id)}
        data-testid="player-row"
        data-player-row={player.id}
        data-carrying={carrying || undefined}
        onPointerDown={
          draggable
            ? (event) => {
                // The controls at the end of the row are gestures of their own.
                // A press on the three dots is the menu opening and a press on
                // the pencil is the sheet, whether it is short or long.
                if ((event.target as HTMLElement).closest('button, a, input, select') !== null) {
                  return;
                }
                playerDragGesture.begin(player.id)(event);
              }
            : undefined
        }
        onClick={
          isYou
            ? (event) => {
                // The click that ends a drag is not a tap on your own card.
                if (playerDragGesture.swallowClick()) return;
                const target = event.target as HTMLElement;
                // A real control inside the card is doing its own job. This is
                // the empty space around them.
                if (target.closest('button, a, input, select') !== null) return;
                // The dot is only worth aiming at while there is a colour in it
                // to change; in a room past `COLORED_SEATS` the sheet has no
                // picker to open on.
                setEditing(
                  roomHasColors && target.closest('[data-player-dot]') !== null ? 'colour' : 'card',
                );
              }
            : undefined
        }
        className={`flex items-center gap-1.5 px-2.5 py-1.5 transition-colors duration-200 ease-out ${
          isYou ? 'cursor-pointer' : ''
        } ${
          // Held rather than hidden. A row that collapsed out of the list would
          // shrink the zone it is leaving, and a zone that shrinks under the
          // pointer is a drop target that flickers between itself and its
          // neighbour. It keeps its place and its size, faded.
          carrying ? 'opacity-40' : ''
        } ${
          // A long press on a phone is also the browser's own "select this text"
          // and "show me a callout" gesture, and both of them fire over a name.
          draggable ? 'touch-manipulation select-none [-webkit-touch-callout:none]' : ''
        } ${
          nested
            ? 'rounded-tile bg-surface-2'
            : `rounded-card border bg-surface ${isYou ? 'border-line-strong' : 'border-line'}`
        }`}
      >
        <div className="min-w-0 flex-1">
          <PlayerChip
            player={player}
            color={seatColor(player)}
            showHostCrown={player.isHost}
            showMuted={player.muted}
            trailing={isYou ? undefined : readyMarker(player)}
          />
        </div>
        {/* The end of the row, and it is at least 44px of something on every
            one of them: your two controls on yours, the three dots on somebody
            else's, and an empty square where there is neither — which is what
            keeps the rows the same height and the ready markers in one column.

            "At least" since the seat toggle gained its label: yours is now 68px
            of word plus 44px of pencil. That is the one row it can be wider on,
            and the one row with no ready marker to line up, so the column is
            unaffected. */}
        {isYou ? (
          editPencil
        ) : host ? (
          <HostRowMenu
            send={send}
            player={player}
            teams={teams}
            open={openMenu === player.id}
            onOpenChange={(next) => setOpenMenu(next ? player.id : null)}
            llmModels={llm?.models ?? []}
            rounds={settings.roundSequence.length}
            aiSeating={aiSeating}
          />
        ) : (
          <span aria-hidden="true" className="h-11 w-11 shrink-0" />
        )}
      </div>
    );
  };

  // --- dragging a round along the plan ---
  const wellRef = useRef<HTMLDivElement | null>(null);
  const cellRefs = useRef(new Map<number, HTMLElement>());
  const dragRef = useRef<DragState | null>(null);
  /**
   * Chip centres as they were when the drag began, relative to the well.
   *
   * Measuring live would feed the layout back into the thing that is changing
   * it: the indicator opens, the chips shift, the nearest chip changes, the
   * indicator moves, and the plan flickers between two positions under a
   * stationary finger. A frozen grid cannot oscillate, and it matches what the
   * host is aiming at — the chips as they were when they picked one up.
   */
  const gridRef = useRef<{ x: number; y: number }[] | null>(null);

  const setDragState = (next: DragState | null) => {
    dragRef.current = next;
    setDrag(next);
  };

  const captureGrid = useCallback(() => {
    const well = wellRef.current;
    if (well === null) return;
    const base = well.getBoundingClientRect();
    const points: { x: number; y: number }[] = [];
    for (let index = 0; index < plan.length; index += 1) {
      const node = cellRefs.current.get(index);
      const rect = node?.getBoundingClientRect();
      points.push(
        rect === undefined
          ? { x: 0, y: 0 }
          : { x: rect.left + rect.width / 2 - base.left, y: rect.top + rect.height / 2 - base.top },
      );
    }
    gridRef.current = points;
  }, [plan.length]);

  const dropIndex = useCallback((clientX: number, clientY: number, from: number): number => {
    const points = gridRef.current;
    const well = wellRef.current;
    if (points === null || well === null) return from;

    // The grid is stored relative to the well, so a page that scrolls mid-drag
    // does not shift every answer by the scroll distance.
    const base = well.getBoundingClientRect();
    return insertionIndex(points, clientX - base.left, clientY - base.top, from);
  }, []);

  /**
   * The plan's own drag, on the gesture both drags share.
   *
   * No `holdMs`: a round chip sets `touch-action: none` and sits in a well that
   * does not scroll, so movement on one can only ever have meant a drag. The
   * seat rows below are the opposite case and say so there.
   */
  const roundDrag = usePointerDrag<number>({
    threshold: DRAG_THRESHOLD,
    // Before the first indicator opens, while the well is still at rest.
    onStart: () => captureGrid(),
    onMove: (from, point) => {
      setDragState({
        from,
        to: dropIndex(point.x, point.y, from),
        x: point.x,
        y: point.y,
      });
    },
    onEnd: (_from, dragged) => {
      const carried = dragRef.current;
      gridRef.current = null;
      setDragState(null);
      if (dragged && carried !== null) moveRound(carried.from, carried.to);
    },
  });

  const removeRound = (index: number) => {
    // The press ends in a click on the chip it started on, and that click means
    // "take this round out".
    if (roundDrag.swallowClick()) return;
    // A game needs at least one round, so the last one stays.
    if (plan.length > 1) pushSequence(plan.filter((_, at) => at !== index));
  };

  const registerCell = (index: number) => (node: HTMLDivElement | null) => {
    if (node === null) cellRefs.current.delete(index);
    else cellRefs.current.set(index, node);
  };

  /** The gap that opens where the carried round will land, in its own colour. */
  const dropSlot = (position: number) => {
    const carried = drag === null ? null : (plan[drag.from] ?? null);
    return (
      <div
        key={`slot-${position}`}
        aria-hidden="true"
        data-testid={drag !== null && drag.to === position ? 'drop-slot' : undefined}
        className="overflow-hidden transition-[width] duration-200 ease-out"
        style={{ width: drag !== null && drag.to === position ? CELL_WIDTH : 0 }}
      >
        <div
          data-drop-type={carried ?? undefined}
          className={`h-14 w-11 rounded-[9px] border-2 border-dashed ${
            carried === null ? 'border-line-strong bg-raised' : DROP_SLOT_TINT[carried]
          }`}
        />
      </div>
    );
  };

  const roundCell = (type: RoundType, index: number) => {
    const carried = drag !== null && drag.from === index;
    return (
      <div
        key={`${index}-${type}`}
        ref={registerCell(index)}
        style={{ width: carried ? 0 : CELL_WIDTH }}
        className={`transition-[width] duration-200 ease-out ${carried ? 'overflow-hidden' : ''}`}
      >
        {host ? (
          <button
            type="button"
            data-testid="round-chip"
            onPointerDown={roundDrag.begin(index)}
            // Drag is a pointer gesture, so it is one code path for mouse, pen
            // and finger. The arrow keys move a round too, because a host on a
            // phone could otherwise add and remove rounds but never order them.
            onKeyDown={(event) => {
              if (event.key === 'ArrowLeft') {
                event.preventDefault();
                moveRound(index, index - 1);
              } else if (event.key === 'ArrowRight') {
                event.preventDefault();
                moveRound(index, index + 1);
              }
            }}
            onClick={() => removeRound(index)}
            // A game needs one round, so the last one cannot be taken out and
            // its label does not offer to.
            aria-label={
              plan.length > 1
                ? `Round ${index + 1}, ${roundLabel(
                    type,
                  )}. Drag to move it, press to take it out, arrow keys to shift it.`
                : `Round 1, ${roundLabel(type)}. The only round in the plan.`
            }
            style={{ touchAction: 'none' }}
            className={`rounded-[9px] transition-opacity duration-150 ease-out ${
              carried ? 'cursor-grabbing opacity-0' : 'cursor-grab opacity-100'
            }`}
          >
            <RoundTypeChip type={type} position={index + 1} />
          </button>
        ) : (
          <div data-testid="round-chip">
            <RoundTypeChip type={type} position={index + 1} />
          </div>
        )}
      </div>
    );
  };

  const wellCells: ReactNode[] = [];
  let slotPosition = 0;
  plan.forEach((type, index) => {
    if (drag !== null && drag.from === index) {
      wellCells.push(roundCell(type, index));
      return;
    }
    wellCells.push(dropSlot(slotPosition));
    wellCells.push(roundCell(type, index));
    slotPosition += 1;
  });
  wellCells.push(dropSlot(slotPosition));

  /**
   * One dashed add button per round type, at the end of the plan.
   *
   * There used to be a single hardcoded `+` that always added a letters round,
   * and three text buttons underneath that added one of each — so the obvious
   * affordance did one third of the job and the row that did the whole job did
   * not look like part of the builder. Three chips say what each one adds and
   * land it where a host is already looking.
   *
   * **They are faded all the way back to the surface, and they are not tinted
   * to the round they add.** They used to wear the type's own border and text
   * colour at the type's own 22px weight, one dashed edge away from the real
   * chip beside them, and hosts read them as rounds already in the plan. So the
   * hierarchy is inverted rather than merely dimmed: the `+` is the mark and
   * the letter is a small mono note under it, both `ink-dim` on nothing, in a
   * single neutral dashed outline. Hover and focus bring one back up, which is
   * where the host finds out it is a button. The box stays 44 by 56, so the tap
   * target and the well's geometry are exactly what they were.
   *
   * **Nothing disables them.** There was a fifteen-round ceiling on the plan and
   * the buttons went dead at it; the ceiling was a default nobody chose, and a
   * room that wants forty rounds is a room having a long evening rather than a
   * room making a mistake. What is left in `packages/shared` is a server guard
   * against an absurd number, not a size the builder should be teaching.
   */
  const addCell = (type: RoundType) => (
    <div key={`add-${type}`} style={{ width: CELL_WIDTH }}>
      <button
        type="button"
        data-testid="add-round"
        data-add-round={type}
        onClick={() => pushSequence([...plan, type])}
        aria-label={`Add a ${roundLabel(type).toLowerCase()} round`}
        title={`Add a ${roundLabel(type).toLowerCase()} round`}
        className="flex h-14 w-11 cursor-pointer flex-col items-center justify-center gap-[3px] rounded-[9px] border border-dashed border-line text-ink-dim transition-colors duration-150 ease-out hover:border-line-strong hover:bg-surface-2 hover:text-ink focus-visible:border-line-strong focus-visible:text-ink"
      >
        <span aria-hidden="true" className="text-[17px] leading-none font-semibold">
          +
        </span>
        <span aria-hidden="true" className="font-mono text-[11px] leading-none">
          {ROUND_CHIP_LETTER[type]}
        </span>
      </button>
    </div>
  );

  return (
    // No scroll container of its own: the room shell already scrolls, and a
    // second one nested inside it would give the sticky footer the wrong thing
    // to stick to.
    <div className="flex min-h-full flex-1 flex-col">
      {/* --- header --- */}
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line px-4 py-3 lg:gap-x-4 lg:px-7 lg:py-4">
        <div className="flex items-baseline gap-2.5 lg:gap-3.5">
          <SectionLabel>room</SectionLabel>
          {/* Masked as asterisks rather than blurred: a blur is a picture of the
              code that a good camera and a still frame can often read back, and
              it says "there is something here to look at". */}
          <span
            data-testid="room-code"
            aria-hidden={codeMasked || undefined}
            className="font-mono text-[30px] leading-none font-bold tracking-[0.14em] text-blue-200 lg:text-[40px]"
          >
            {codeMasked ? '*'.repeat(state.roomCode.length) : state.roomCode}
          </span>
          {codeMasked ? <span className="sr-only">Room code hidden</span> : null}
        </div>

        {/* Two marks rather than two 110px words. Each carries its own name for
            a screen reader and its own tooltip for a mouse, which is what the
            words were there for, and the tick that replaces the copy mark is
            the feedback the changing label used to give.

            Both are on every screen now. They used to disappear for anybody the
            host had hidden the code from, which was a room setting that has
            since been deleted — it masked the code in the UI while leaving it in
            everybody's address bar. See `codeMasked`. */}
        <>
          <IconButton
              size="sm"
              variant="ghost"
              label={copied ? 'Link copied' : 'Copy link'}
              title={copied ? 'Link copied' : 'Copy link'}
              onClick={copyLink}
            >
              <Icon
                icon={copied ? CheckIcon : CopyIcon}
                size={16}
                className={copied ? 'text-good' : ''}
              />
            </IconButton>
            <IconButton
              size="sm"
              variant="ghost"
              role="switch"
              aria-checked={codeHidden}
              label={codeHidden ? 'Show code' : 'Hide code'}
              title="Masks the code on screen. Copy link still works."
              onClick={() => hideCode(!codeHidden)}
            >
            <Icon icon={codeHidden ? EyeIcon : EyeOffIcon} size={16} />
          </IconButton>
        </>

        {/* The room's privacy, said out loud where the code is read out loud.
            The same state as the Visibility segment under Settings, and the
            same write — a host can change it from either, and neither can show
            the other's answer as stale, because both render `settings`. */}
        {host ? (
          <Button
            variant="outline"
            size="sm"
            data-testid="privacy-chip"
            title={
              isPrivate
                ? 'Only people with the code can find this room. Press to make it public.'
                : 'Anybody can find this room in the list. Press to make it private.'
            }
            onClick={() => patch({ visibility: isPrivate ? 'public' : 'private' })}
          >
            <Icon icon={isPrivate ? LockIcon : GlobeIcon} size={14} />
            {isPrivate ? 'Private' : 'Public'}
          </Button>
        ) : (
          <span
            data-testid="privacy-chip"
            className="flex h-11 items-center gap-2 rounded-[9px] px-2 text-[13px] font-semibold text-ink-muted"
          >
            <Icon icon={isPrivate ? LockIcon : GlobeIcon} size={14} />
            {isPrivate ? 'Private' : 'Public'}
          </span>
        )}

        {/* The controls that survive the start of a game, one press from the
            top of the room. It is a mark rather than a word for the same reason
            copy and mask are: this header is above the thing anybody came here
            to read, and five labelled controls is 380px of chrome over it.

            In the lobby everything behind it is also in the column below, which
            is the point — a host finds it here in the phase where finding it is
            easy, and it is in the same place in the phase where the column is
            not on screen at all. */}
        {host ? (
          <IconButton
            size="sm"
            variant="ghost"
            data-testid="open-safety"
            aria-expanded={safetyOpen}
            label="Room controls"
            title="Chat, the lock and who can do what. Changeable during a game."
            onClick={() => setSafetyOpen(true)}
          >
            <Icon icon={ShieldCheckIcon} size={16} />
          </IconButton>
        ) : null}

        {/* Last in the row, so the label is free to grow leftward into the
            space rather than being padded out to its widest string. */}
        <div className="ml-auto flex items-center">
          <ConnectionStatus quality={quality} />
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-[1180px] flex-1 flex-col gap-4 px-4 py-4 lg:flex-row lg:items-start lg:gap-7 lg:px-7 lg:py-5">
        {/* --- one list, teams and solo players together --- */}
        <section aria-label="Players" className="flex flex-col gap-2.5 lg:w-[370px] lg:shrink-0">
          {/* The door, on top of the list it decides the contents of. */}
          <RoomDoor
            host={host}
            lock={settings.roomLock ?? 'open'}
            onChange={(next) => patch({ roomLock: next })}
          />

          <div className="flex flex-wrap items-center gap-2">
            {/* A count, and a fraction only when there is something to count
                against. The default room has no seat limit, so `2 of 8` would be
                reporting a ceiling nobody chose — the same mistake `9 of 15` was
                making over the round plan. */}
            <SectionLabel>
              players {seated.length}
              {settings.maxPlayers === null ? '' : ` of ${settings.maxPlayers}`}
            </SectionLabel>
            {joinPlayersControl === null ? null : (
              <span className="ml-auto shrink-0">{joinPlayersControl}</span>
            )}
            {/* Only once there is somewhere to drag a row to and somebody
                allowed to do it, and worded for the gesture this device actually
                has: a cursor starts on movement, a finger has to wait. The round
                sequence carries the same kind of note over its own well. */}
            {teamed && canPickTeam ? (
              <span
                data-testid="drag-hint"
                className="font-mono text-[11px] font-semibold tracking-[0.08em] text-blue-200 uppercase"
              >
                {isDesktop ? 'drag to move' : 'hold to move'}
              </span>
            ) : null}
            {canMakeTeams ? (
              <Button
                variant="outline"
                size="sm"
                className="ml-auto"
                disabled={teams.length >= TEAM_COLORS.length}
                onClick={createTeam}
              >
                Create team
              </Button>
            ) : null}
          </div>

          {/* Teams sort above individuals so a half-teamed room still reads top to
              bottom, and a team is a container rather than a badge on a row.
              They come from `state.teams`, which is why everybody in the room
              sees the same ones. */}
          {teams.map((team) => {
            const members = membersOfTeam(state, team.id);
            return (
              <div
                key={team.id}
                data-testid="team"
                data-team-id={team.id}
                // The whole card is the target, header included: a team is one
                // thing on screen and aiming at the part of it that happens to
                // hold rows is a rule nobody could see.
                data-drop-zone={team.id}
                // **No `overflow-hidden` here, and that is load-bearing.** The
                // host's row menu is a popover anchored `absolute` inside the
                // row it belongs to — deliberately, because the seat list
                // scrolls and a viewport-positioned menu slides off its own row
                // the moment it does (see the comment on `HostRowMenu`). A team
                // is the only container between that popover and the column, so
                // a clip on it cut every menu opened inside a team off at the
                // card's edge. The only thing the clip was doing was keeping the
                // tinted header off the card's rounded top corners, and the
                // header rounds its own corners now.
                className="rounded-card border transition-colors duration-200 ease-out"
                style={{
                  borderColor: team.color,
                  background: `color-mix(in oklab, ${team.color} 14%, var(--c-surface))`,
                }}
              >
                <div
                  // Its own top corners, because the card no longer clips it.
                  className="flex min-h-11 items-center gap-2.5 rounded-t-card px-3 py-2"
                  style={{ background: `color-mix(in oklab, ${team.color} 34%, var(--c-surface))` }}
                >
                  <span
                    aria-hidden="true"
                    className="shrink-0 rounded-[3px]"
                    style={{ width: 5, height: 16, background: team.color }}
                  />
                  <span className="text-[14px] font-bold text-ink">{team.name}</span>
                  <span className="font-mono text-[12px] text-ink-muted tabular">
                    {members.length === 1 ? '1 player' : `${members.length} players`}
                  </span>
                  <div className="ml-auto flex items-center gap-1">
                    {/* Joining a team that already exists is choosing where to
                        sit, not changing the shape of the lobby, which is why it
                        has its own setting and why that setting defaults the
                        other way. A host who wants to do the seating turns it
                        off, and then there is nothing here to press — the moves
                        are all in the host's row menu. Leaving goes with it: a
                        room where anybody can walk off a side the host put them
                        on is a room where the host is not doing the seating. */}
                    {canPickTeam && youAreSeated && you?.teamId !== team.id ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="font-mono tracking-[0.08em] uppercase"
                        onClick={() => send({ type: 'join-team', teamId: team.id })}
                      >
                        join
                      </Button>
                    ) : canPickTeam && you?.teamId === team.id ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="font-mono tracking-[0.08em] uppercase"
                        onClick={() => send({ type: 'join-team', teamId: null })}
                      >
                        leave
                      </Button>
                    ) : null}
                    {/* An empty team is nobody's, so anybody the room's
                        permissions allow may clear it away — which by default is
                        the host alone, on the same reasoning as opening one.
                        A team with players in it is not deleted out from under
                        them whoever presses. */}
                    {permitted && members.length === 0 ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Delete ${team.name}`}
                        className="font-mono tracking-[0.08em] text-ink-dim uppercase hover:text-alert"
                        onClick={() => send({ type: 'delete-team', teamId: team.id })}
                      >
                        delete
                      </Button>
                    ) : null}
                  </div>
                </div>
                <div className="flex flex-col gap-2 p-2">
                  {members.length === 0 && !previewIn(team.id) ? (
                    <p className="px-1 py-2 text-[13px] text-ink-muted">Nobody here yet.</p>
                  ) : (
                    members.map((member) => playerRow(member, true))
                  )}
                  {previewIn(team.id) ? playerDropSlot(team.color) : null}
                </div>
              </div>
            );
          })}

          {/* The other drop target, and the reason it is a container at all.
              Solo rows were loose children of this column, which is fine to
              look at and impossible to aim at: "not on a team" needs a box
              before it can be a place to drop somebody. It keeps the column's
              own spacing, so nothing about the list moved.

              When it is empty it has no height either, so while a drag is live
              it grows a strip — otherwise the one way out of a team would be a
              target with nothing to hit. */}
          <div
            data-drop-zone={UNTEAMED}
            data-testid="unteamed"
            className="flex flex-col gap-2.5 empty:hidden"
          >
            {solo.map((player) => playerRow(player, false))}
            {draggedPlayer !== null && previewIn(UNTEAMED) ? (
              playerDropSlot(colorInZone(draggedPlayer, UNTEAMED))
            ) : playerDrag !== null && solo.length === 0 ? (
              <div
                aria-hidden="true"
                data-testid="unteamed-landing"
                className="grid min-h-14 place-items-center rounded-card border-2 border-dashed border-line text-[13px] text-ink-dim"
              >
                On their own
              </div>
            ) : null}
          </div>

          {/* The foot of the list, and the two things that belong at the foot of
              a list of seats: how many are free, and how to fill one.

              **The add panel used to be a card of its own above the roster**,
              carrying a section label, a four-way control, a button and a row
              held permanently open for a model picker — about 140px of chrome
              on a phone, most of it empty, sitting between the header and the
              people in the room. It is a dashed row where the empty seats
              already were now, and it opens in place. Nothing above it moves
              when it does, because there is nothing above it that it can push.

              A non-host gets the seat count and no button: `add-bot` is refused
              from anybody else, so an opener would be a door into a room the
              server will not let them enter. **A full room gets the same
              treatment, for the same reason.** It used to render the opener
              disabled, which is the harder thing to read of the two: a dead
              control with no explanation beside it reads as broken, and the
              explanation was there — "Every seat taken", at the other end of
              the same row — but it was competing with a plus sign and the words
              "Add an opponent" for what the row was about. There is nothing to
              add, so the row simply says how full the room is. If it is open
              when the last seat goes, it closes. */}
          {/* Nothing at all for a guest in a room with no seat limit: the row's
              whole job for them is the seat count, and a room with no cap has
              no count to report. See `openSeats`. */}
          {canAdd || seatsCopy !== null ? (
          <div
            data-testid="open-seats"
            className={`rounded-card border border-dashed transition-colors duration-200 ease-out ${
              addOpen ? 'border-line-strong bg-surface' : 'border-line'
            }`}
          >
            {canAdd ? (
              <button
                type="button"
                data-testid="add-opponent"
                aria-expanded={addOpen}
                onClick={() => setAddOpen(!addOpen)}
                className="flex min-h-11 w-full cursor-pointer items-center gap-2 px-3 text-left text-[14px] text-ink-dim transition-colors duration-150 ease-out hover:text-ink disabled:cursor-not-allowed disabled:hover:text-ink-dim"
              >
                <Icon icon={PlusIcon} size={16} />
                <span>Add an opponent</span>
                {seatsCopy === null ? null : (
                  <span className="ml-auto font-mono text-[11px] tracking-[0.08em] uppercase tabular">
                    {seatsCopy}
                  </span>
                )}
              </button>
            ) : (
              <div className="flex min-h-11 items-center px-3">
                <span className="text-[14px] text-ink-dim">{seatsCopy}</span>
              </div>
            )}

            {/* Bots are chosen before they are seated. Difficulty used to be
                hardcoded to medium at both call sites and changeable only
                afterwards, from a menu on the row the bot had already taken.

                **Two controls and one button.** It was one segment listing every
                difficulty with `AI` on the end of it, and five difficulties is
                what ended that: a four-way segment and a button already did not
                fit on a 319px card, and six options is a row of slivers. The
                kind is the question that comes first and it has two answers; how
                strong a bot should be is the second one, and it exists for only
                one of them. */}
            {canAdd && addOpen ? (
              <div className="flex flex-col gap-2 border-t border-line px-3 py-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  {/* The kind, the difficulty, the model question and the
                      button are stacked rows rather than a row that wraps. On a
                      319px card what wrapping produced was the button alone on
                      the second line with an inch of nothing beside it. */}
                  <Segmented
                    label="Opponent"
                    size="sm"
                    value={opponentKind}
                    options={[
                      { value: 'bot' as OpponentKind, label: 'Bot' },
                      // The AI option is on screen for a signed-out host too,
                      // greyed and saying why beneath. Hiding it made a room
                      // look like a site with no AI opponents in it, which is a
                      // worse answer than a control that cannot be pressed yet.
                      // Absent entirely when this server has AI switched off,
                      // and while `/api/config` is still out: there is then
                      // nothing to unlock, so an option offering it would be a
                      // lie rather than a locked door.
                      ...(aiSeating === null || configLoading
                        ? []
                        : [
                            {
                              value: 'ai' as OpponentKind,
                              label: 'AI',
                              disabled: aiSeating.blockedReason !== null,
                            },
                          ]),
                    ]}
                    onChange={setNewOpponentKind}
                  />
                </div>

                {/* Only for a bot. A difficulty on an AI opponent is a strength
                    dial wired to nothing — see `addOpponent` — so the question
                    goes away rather than being asked and ignored. */}
                {opponentKind === 'bot' ? (
                  <StopSlider
                    label="Difficulty"
                    showLabel
                    stops={BOT_DIFFICULTIES}
                    labelFor={(difficulty) => DIFFICULTY_LABEL[difficulty]}
                    value={newOpponentDifficulty}
                    onChange={setNewOpponentDifficulty}
                  />
                ) : null}

                {/* No reserved height under the choice any more: this panel is
                    the last thing in the list, so a model picker arriving pushes
                    nothing down except the fold. It cost 44px of empty space at
                    the bottom of every phone screen to hold a row that is
                    usually not needed. */}
                {aiSeating === null || configLoading ? null : opponentKind === 'ai' &&
                  aiSeating.blockedReason === null ? (
                  <div data-testid="ai-row" className="flex items-center gap-2">
                    <label htmlFor="new-opponent-model" className="label-mono shrink-0 text-ink-muted">
                      model
                    </label>
                    {/* `min-w-0 flex-1` so a long model id shortens the select
                        rather than wrapping the row onto a second line. */}
                    <select
                      id="new-opponent-model"
                      value={aiModel ?? ''}
                      onChange={(event) => setNewOpponentModel(event.target.value)}
                      className="min-h-11 min-w-0 flex-1 rounded-card border border-line bg-surface-2 px-2 text-[13px] text-ink"
                    >
                      {llm?.models.map((model) => (
                        <option key={model.id} value={model.id}>
                          {model.label}
                        </option>
                      ))}
                    </select>
                    {/* A `Thinking` switch was here and is gone with
                        `REASONING_AVAILABLE`. It is the second of the two
                        controls that could turn native reasoning on — the other
                        was in `HostRowMenu` — and removing one without the other
                        would have left the expensive path a tap away. */}
                  </div>
                ) : aiSeating.blockedReason === null ? null : (
                  // Only when the option cannot be taken. Working, the segment
                  // above says what it does on it.
                  <span data-testid="ai-row" className="text-[12px] text-ink-muted">
                    {aiSeating.blockedReason}
                  </span>
                )}

                {/* Its own row, the width of the panel. Beside the segment it
                    wrapped onto a second line on every phone and sat there with
                    an inch of nothing to its left. */}
                <Button
                  variant="outline"
                  size="sm"
                  block
                  disabled={
                    openSeats === 0 ||
                    (opponentKind === 'ai' && (aiSeating?.blockedReason != null || aiModel === null))
                  }
                  onClick={addOpponent}
                >
                  {opponentKind === 'ai' ? 'Add AI' : 'Add bot'}
                </Button>

                {/* The whole catalogue in one press, under the button that
                    seats one. It is not a third option on the kind segment: the
                    segment says what the button above it will seat, and a
                    preset that seats eight opponents and moves a setting is a
                    different sort of thing from a choice about the next one.

                    **A count, and two labels rather than one.** `One of every
                    model` is a promise, and in a room close to the cap it is a
                    promise the press cannot keep — so a room where something
                    has to be left out says so before the press rather than in
                    a toast after it. With nothing left to seat there is no
                    button at all, for the same reason the opener disappears in
                    a full room: a dead control with the explanation somewhere
                    else reads as broken. */}
                {presetOffered ? (
                  presetFits > 0 ? (
                    <Button
                      variant="outline"
                      size="sm"
                      block
                      data-testid="seat-every-model"
                      onClick={seatEveryModel}
                    >
                      {presetFits === unseatedModels.length
                        ? `One of every model (${presetFits})`
                        : `As many models as fit (${presetFits})`}
                    </Button>
                  ) : (
                    <span data-testid="seat-every-model-note" className="text-[12px] text-ink-muted">
                      {unseatedModels.length === 0
                        ? 'Every model has a seat.'
                        : 'No room for another model with seats kept open for people.'}
                    </span>
                  )
                ) : null}
              </div>
            ) : null}
          </div>
          ) : null}

          <div className="flex flex-col gap-2 border-t border-line pt-3">
            <div className="flex flex-wrap items-center gap-2">
              <SectionLabel>
                audience {watching.length}
                {settings.maxSpectators == null ? '' : ` of ${settings.maxSpectators}`}
              </SectionLabel>
              {/* The count is still the count. Somebody who was already watching
                  when the door shut is still watching, and a section that hid
                  itself would take them off the screen the host moderates them
                  from. */}
              {door === 'room' ? (
                <span data-testid="watching-off">
                  <Badge tone="muted">closed</Badge>
                </span>
              ) : null}
              {watchControl === null ? null : (
                <span className="ml-auto shrink-0">{watchControl}</span>
              )}
            </div>
            {/* No "nobody is watching" line: the label above already reads
                `audience 0`. */}
            {watching.length === 0 ? null : (
              <div className="flex flex-wrap gap-2">
                {watching.map((player) => (
                  <div
                    key={player.id}
                    onClick={
                      player.id === state.youId
                        ? (event) => {
                            const target = event.target as HTMLElement;
                            if (target.closest('button, a, input, select') !== null) return;
                            setEditing(
                              target.closest('[data-player-dot]') === null ? 'card' : 'colour',
                            );
                          }
                        : undefined
                    }
                    className={`flex items-center gap-1.5 rounded-tile bg-surface-2 px-2.5 py-1.5 ${
                      player.id === state.youId ? 'cursor-pointer' : ''
                    }`}
                  >
                    {/* Spectators edit their name and colour too, and take a
                        seat from the same mark a player steps out with. Your
                        seat is the only thing that changes when you step out of
                        the game. Beside the chip rather than inside it, for the
                        same two reasons the seat rows put them there. */}
                    <PlayerChip
                      player={player}
                      color={seatColor(player)}
                      size="sm"
                    />
                    {player.id === state.youId ? (
                      editPencil
                    ) : host ? (
                      // **The bench needs the host's menu as much as the seats
                      // do, and it did not have one.** `assign-role` is the way
                      // off the bench, and the only place it is offered is this
                      // menu — so without it here a locked room is a room whose
                      // host can send people to the bench and never bring one
                      // back. Everything else in the menu (mute, kick, ban) is
                      // as true of a spectator as of a player; the team moves
                      // take themselves off for somebody who is not playing.
                      <HostRowMenu
                        send={send}
                        player={player}
                        teams={teams}
                        open={openMenu === player.id}
                        onOpenChange={(next) => setOpenMenu(next ? player.id : null)}
                        llmModels={llm?.models ?? []}
                        rounds={settings.roundSequence.length}
                        aiSeating={aiSeating}
                      />
                    ) : null}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* --- round sequence and settings --- */}
        <section aria-label="Game setup" className="flex min-w-0 flex-1 flex-col gap-3 lg:gap-4">
          <div className="flex items-center gap-2.5">
            <SectionLabel>round sequence</SectionLabel>
            {/* No count here. `RoundSequenceSummary` directly below already
                opens with the number of rounds, so this was the same fact twice
                within about forty pixels of itself. */}
            {host ? (
              <span className="ml-auto hidden font-mono text-[11px] font-semibold tracking-[0.08em] text-blue-200 uppercase lg:inline">
                drag to reorder
              </span>
            ) : (
              <span className="ml-auto">
                <Badge tone="muted">host only</Badge>
              </span>
            )}
          </div>

          <RoundSequenceSummary sequence={plan} showEstimate settings={settings} />

          <div
            ref={wellRef}
            data-testid="sequence-well"
            className="flex flex-wrap items-start gap-y-1.5 rounded-card border border-line bg-bg-alt p-2.5 lg:p-3"
          >
            {wellCells}
            {host ? (['letters', 'numbers', 'conundrum'] as const).map(addCell) : null}
          </div>

          {host ? (
            <div className="flex flex-wrap items-center gap-2">
              <SectionLabel>presets</SectionLabel>
              {ROUND_SEQUENCE_PRESETS.map((preset) => (
                <Button
                  key={preset.id}
                  variant="outline"
                  size="sm"
                  // The plan only. A preset that also moved the clocks would
                  // undo a host's own timing every time they tried a shape.
                  onClick={() => pushSequence([...preset.sequence])}
                >
                  {PRESET_LABEL[preset.id] ?? preset.label}
                </Button>
              ))}
            </div>
          ) : null}

          <div className="h-px bg-line" />

          <div className="flex items-center gap-2.5">
            <SectionLabel>settings</SectionLabel>
            {host ? <Badge tone="host">you host</Badge> : <Badge tone="muted">host only</Badge>}
          </div>

          {/* Two whole-room setups, one press each.
              **Above the settings rather than under them**, because a press
              here writes about ten of the controls below it, and a shortcut that
              undoes what you have just finished setting is a shortcut in the
              wrong place.

              One-way, and there is nothing to turn off: each one stamps the
              settings and stands aside, so everything it wrote is an ordinary
              control the host can move afterwards. See `ROOM_PRESETS`, which
              also says why this is no longer filed under `streaming`. */}
          {host ? (
            <div data-testid="room-presets" className="flex flex-col gap-2">
              {/* Not `presets`: the round plans a few inches above are already
                  called that, and one screen with two unrelated PRESETS headings
                  on it makes the reader work out which is which every time. */}
              <SectionLabel>quick setup</SectionLabel>
              <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
                {ROOM_PRESETS.map((preset) => (
                  <div key={preset.id} className="flex flex-col gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      block
                      data-preset={preset.id}
                      onClick={() => patch(preset.settings)}
                    >
                      {preset.label}
                    </Button>
                    <span className="text-[12px] text-ink-muted">{preset.hint}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-2 lg:gap-3">
            {/* The three clocks that decide how the game feels, together, in the
                order the rounds are played in. They no longer move as one: the
                defaults deliberately differ, so a single control across all
                three would sit on "mixed" in an ordinary room.
                No sentence under any of them: a slider labelled "Letters round"
                reading 30s has already said what it is. */}
            <SettingsCard wide>
              <TimerField
                label="Letters round"
                editable={host}
                value={settings.lettersPlaySeconds}
                onChange={(next) => patch({ lettersPlaySeconds: next })}
              />
              <CardRule />
              <TimerField
                label="Numbers round"
                editable={host}
                value={settings.numbersPlaySeconds}
                onChange={(next) => patch({ numbersPlaySeconds: next })}
              />
              <CardRule />
              <TimerField
                label="Conundrum round"
                editable={host}
                value={settings.conundrumSeconds}
                onChange={(next) => patch({ conundrumSeconds: next })}
              />
            </SettingsCard>

            <SettingsCard wide>
              {host ? (
                <Toggle
                  checked={settings.lettersWordCheck}
                  onChange={(next) => patch({ lettersWordCheck: next })}
                  label={WORD_CHECK_LABEL}
                  description={WORD_CHECK_HINT}
                />
              ) : (
                <ReadOnlyRow
                  label={WORD_CHECK_LABEL}
                  hint={WORD_CHECK_HINT}
                  value={settings.lettersWordCheck ? 'on' : 'off'}
                />
              )}
            </SettingsCard>

            {/* Beside the other word-round rule rather than with the AI seats,
                because it is a rule about what counts as an answer and the row
                menu is about who is playing. Shown whether or not a model is
                seated: it decides how the room behaves when one is. */}
            <SettingsCard wide>
              {host ? (
                <Toggle
                  checked={settings.llmPrefersRealWords}
                  onChange={(next) => patch({ llmPrefersRealWords: next })}
                  label={REAL_WORDS_LABEL}
                  description={REAL_WORDS_HINT}
                />
              ) : (
                <ReadOnlyRow
                  label={REAL_WORDS_LABEL}
                  hint={REAL_WORDS_HINT}
                  value={settings.llmPrefersRealWords ? 'on' : 'off'}
                />
              )}
            </SettingsCard>

            {/* Out here rather than under Advanced, because it is the one
                setting in this list a room is most likely to want changed while
                something is going wrong — which is also why it is in
                `SAFETY_SETTINGS` and in the panel the header opens. */}
            <SettingRow
              label={CHAT_LABEL}
              hint={CHAT_HINT[settings.chatMode]}
              control={
                host ? (
                  <Segmented
                    label={CHAT_LABEL}
                    size="sm"
                    value={settings.chatMode}
                    options={CHAT_OPTIONS}
                    onChange={(next) => patch({ chatMode: next })}
                  />
                ) : (
                  <StaticValue>
                    {CHAT_OPTIONS.find((option) => option.value === settings.chatMode)?.label}
                  </StaticValue>
                )
              }
            />

            {/* The other end of the header chip. Two controls, one state: this
                one names both options at once, which is what a settings column
                is for, and the chip is the one you can read while saying the
                code out loud. */}
            <SettingRow
              label={VISIBILITY_LABEL}
              hint={VISIBILITY_HINT(isPrivate)}
              control={
                host ? (
                  <Segmented
                    label={VISIBILITY_LABEL}
                    size="sm"
                    value={settings.visibility}
                    options={[
                      { value: 'public', label: 'Public' },
                      { value: 'private', label: 'Private' },
                    ]}
                    onChange={(next) => patch({ visibility: next })}
                  />
                ) : (
                  <StaticValue>{isPrivate ? 'Private' : 'Public'}</StaticValue>
                )
              }
            />
          </div>

          {/* Everything a room can be played without. Collapsed on every screen
              size, not only on a phone. */}
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={advanced}
            className="self-start"
            onClick={() => setAdvanced(!advanced)}
          >
            {advanced ? 'Hide advanced' : 'Advanced'}
          </Button>

          {advanced ? (
            <div
              data-testid="advanced-settings"
              className="grid grid-cols-1 gap-2.5 lg:grid-cols-2 lg:gap-3"
            >
              <SettingsGroup title="clocks">
              <SettingsCard wide>
                <TimerField
                  label="Letters selection"
                  editable={host}
                  value={settings.lettersSelectionSeconds}
                  onChange={(next) => patch({ lettersSelectionSeconds: next })}
                />
                <CardRule />
                <TimerField
                  label="Numbers selection"
                  editable={host}
                  value={settings.numbersSelectionSeconds}
                  onChange={(next) => patch({ numbersSelectionSeconds: next })}
                />
                <CardRule />
                <TimerField
                  label="Conundrum buzz"
                  editable={host}
                  min={BUZZ_MIN_SECONDS}
                  max={buzzMax}
                  value={settings.conundrumBuzzAnswerSeconds}
                  onChange={(next) => patch({ conundrumBuzzAnswerSeconds: next })}
                />
              </SettingsCard>
              </SettingsGroup>
              <SettingsGroup title="the room">
              {/* **Two numbers of the same shape, so they share a card and one
                  sentence.** They were two cards, each with a switch, a stepper
                  and a paragraph — six rows and about fifty words to express two
                  numbers that are both off by default and that most rooms never
                  touch. Off is `null` rather than a separate switch, so a limit
                  is one control instead of two.

                  The cap is not the room's door and the one sentence on the card
                  says so. It was eight players against a ceiling of sixteen and
                  the ceiling was really the palette talking (`COLORED_SEATS`);
                  what is left is the host who genuinely wants "six of us and no
                  more". */}
              <SettingsCard wide>
                <LimitRow
                  label={PLAYER_LIMIT_LABEL}
                  editable={host}
                  value={settings.maxPlayers}
                  // Never below the number of people already playing: a limit
                  // that arrives smaller than the room is one the server refuses
                  // and one somebody is already sitting outside of.
                  min={Math.max(MAX_PLAYERS_MIN, seated.length)}
                  max={MAX_PLAYERS_HARD_CAP}
                  onChange={(next) => patch({ maxPlayers: next })}
                />
                <CardRule />
                <LimitRow
                  label={AUDIENCE_LIMIT_LABEL}
                  editable={host}
                  value={settings.maxSpectators}
                  min={MAX_SPECTATORS_MIN}
                  max={MAX_SPECTATORS_CAP}
                  step={5}
                  onChange={(next) => patch({ maxSpectators: next })}
                />
                <p className="pt-1 text-[12px] text-ink-muted">{LIMITS_HINT}</p>
              </SettingsCard>
              {/* **Two permissions in one card, because the difference between
                  them is the thing worth reading.** Making a team changes the
                  shape of the lobby and is off by default — one button between
                  an ordinary room and a hundred teams once an audience is in it.
                  Joining one that already exists is choosing where to sit, and a
                  host who has opened four teams has already said the teams are
                  there to be joined, so it is on. Side by side the two labels
                  say which is which. */}
              <SettingsCard wide>
                {host ? (
                  <>
                    <Toggle
                      checked={settings.playerPermissions}
                      onChange={(next) => patch({ playerPermissions: next })}
                      label={PERMISSIONS_LABEL}
                      description={PERMISSIONS_HINT}
                    />
                    <CardRule />
                    <Toggle
                      checked={settings.allowTeamSelfSelect}
                      onChange={(next) => patch({ allowTeamSelfSelect: next })}
                      label={SELF_SELECT_LABEL}
                      description={SELF_SELECT_HINT}
                    />
                  </>
                ) : (
                  <>
                    <ReadOnlyRow
                      label={PERMISSIONS_LABEL}
                      hint={PERMISSIONS_HINT}
                      value={settings.playerPermissions ? 'on' : 'off'}
                    />
                    <CardRule />
                    <ReadOnlyRow
                      label={SELF_SELECT_LABEL}
                      hint={SELF_SELECT_HINT}
                      value={settings.allowTeamSelfSelect ? 'on' : 'off'}
                    />
                  </>
                )}
              </SettingsCard>
              <SettingsCard wide>
                {host ? (
                  <Toggle
                    checked={settings.pauseOnDisconnect}
                    onChange={(next) => patch({ pauseOnDisconnect: next })}
                    label={PAUSE_LABEL}
                  />
                ) : (
                  <ReadOnlyRow
                    label={PAUSE_LABEL}
                    value={settings.pauseOnDisconnect ? 'on' : 'off'}
                  />
                )}
              </SettingsCard>
              </SettingsGroup>
              <SettingsGroup title="the reveal">
              {/* `auto` is a threshold and the other two are not, which is why
                  this is three positions rather than a switch: forcing it on at
                  four players is a legitimate thing to want to look at, and
                  there is no way to ask for that from a threshold. */}
              <SettingRow
                label={AGGREGATE_LABEL}
                hint={
                  settings.aggregateReveal === 'auto'
                    ? `${AGGREGATE_HINT.auto} That is ${LARGE_ROOM_FROM} seats.`
                    : AGGREGATE_HINT[settings.aggregateReveal]
                }
                control={
                  host ? (
                    <Segmented
                      label={AGGREGATE_LABEL}
                      size="sm"
                      value={settings.aggregateReveal}
                      options={AGGREGATE_OPTIONS}
                      onChange={(next) => patch({ aggregateReveal: next })}
                    />
                  ) : (
                    <StaticValue>
                      {
                        AGGREGATE_OPTIONS.find(
                          (option) => option.value === settings.aggregateReveal,
                        )?.label
                      }
                    </StaticValue>
                  )
                }
              />
              {/* How long the reveal holds. One control over the lead, every
                  entry, the floor and the closing beats together, because a
                  room that finds the reveal slow finds all of it slow. */}
              <SettingsCard wide>
                {/* A slider of its own rather than a `TimerField`: that one
                    steps in fives, which on a multiplier reads as `1.1×` for a
                    value of 1.05. Tens land on numbers the readout can tell the
                    truth about. */}
                <Slider
                  label="Reveal pace"
                  showLabel
                  readOnly={!host}
                  min={REVEAL_PACE_MIN}
                  max={REVEAL_PACE_MAX}
                  step={REVEAL_PACE_STEP}
                  value={settings.revealPacePercent}
                  format={REVEAL_PACE_MARKS}
                  describeValue={REVEAL_PACE_SPOKEN}
                  onChange={(next) => patch({ revealPacePercent: next })}
                />
              </SettingsCard>
              </SettingsGroup>
              <SettingsGroup title="the audience">
              <SettingsCard wide>
                {host ? (
                  <Toggle
                    checked={settings.conundrumBuzzer}
                    onChange={(next) => patch({ conundrumBuzzer: next })}
                    label={BUZZER_LABEL}
                    description={BUZZER_HINT}
                  />
                ) : (
                  <ReadOnlyRow
                    label={BUZZER_LABEL}
                    hint={BUZZER_HINT}
                    value={settings.conundrumBuzzer ? 'on' : 'off'}
                  />
                )}
              </SettingsCard>
              {/* The audience, and its two follow-up questions. They are here
                  rather than always on screen for the same reason the model
                  picker is: a question that only applies once something else is
                  on should not be sitting there meaning nothing. */}
              <SettingsCard wide>
                {host ? (
                  <Toggle
                    checked={settings.audienceSeat}
                    onChange={(next) => patch({ audienceSeat: next })}
                    label={AUDIENCE_LABEL}
                    description={AUDIENCE_HINT}
                  />
                ) : (
                  <ReadOnlyRow
                    label={AUDIENCE_LABEL}
                    hint={AUDIENCE_HINT}
                    value={settings.audienceSeat ? 'on' : 'off'}
                  />
                )}
                {settings.audienceSeat ? (
                  <>
                    <CardRule />
                    <div className="flex min-h-11 flex-wrap items-center gap-2">
                      <span className="flex min-w-0 flex-col gap-1">
                        <span className="text-[14px] font-medium text-ink">
                          {AUDIENCE_AGGREGATION_LABEL}
                        </span>
                        <span className="text-[12px] text-ink-muted">
                          {AUDIENCE_AGGREGATION_HINT[settings.audienceAggregation]}
                        </span>
                      </span>
                      <span className="ml-auto shrink-0">
                        {host ? (
                          <Segmented
                            label={AUDIENCE_AGGREGATION_LABEL}
                            size="sm"
                            value={settings.audienceAggregation}
                            options={AUDIENCE_AGGREGATION_OPTIONS}
                            onChange={(next) => patch({ audienceAggregation: next })}
                          />
                        ) : (
                          <StaticValue>
                            {
                              AUDIENCE_AGGREGATION_OPTIONS.find(
                                (option) => option.value === settings.audienceAggregation,
                              )?.label
                            }
                          </StaticValue>
                        )}
                      </span>
                    </div>
                    <CardRule />
                    {host ? (
                      <Toggle
                        checked={settings.audienceCanWin}
                        onChange={(next) => patch({ audienceCanWin: next })}
                        label={AUDIENCE_WINS_LABEL}
                        description={AUDIENCE_WINS_HINT}
                      />
                    ) : (
                      <ReadOnlyRow
                        label={AUDIENCE_WINS_LABEL}
                        hint={AUDIENCE_WINS_HINT}
                        value={settings.audienceCanWin ? 'on' : 'off'}
                      />
                    )}
                  </>
                ) : null}
              </SettingsCard>
              </SettingsGroup>
              <SettingsGroup title="rules">
              {/* The switch that costs the room its statistics.

                  **The price is a warning under the switch, and only once the
                  switch is on.** It used to be a sentence in the description
                  slot, in the same grey as every other hint on this screen —
                  where it read as a note about the setting rather than as its
                  consequence. A host scanning fifteen controls does not read the
                  fifteen grey lines under them. Shown after the press rather
                  than before it because it describes a state the room is now in;
                  the label alone is enough to decide with. */}
              <SettingsCard wide>
                {host ? (
                  <Toggle
                    checked={settings.hostRulings}
                    onChange={(next) => patch({ hostRulings: next })}
                    label={RULINGS_LABEL}
                    description={RULINGS_HINT}
                  />
                ) : (
                  <ReadOnlyRow
                    label={RULINGS_LABEL}
                    hint={RULINGS_HINT}
                    value={settings.hostRulings ? 'on' : 'off'}
                  />
                )}
                {settings.hostRulings ? (
                  <p
                    data-testid="rulings-warning"
                    className="mt-1 rounded-chip bg-accent/15 px-2.5 py-2 text-[12px] text-accent"
                  >
                    {RULINGS_WARNING}
                  </p>
                ) : null}
              </SettingsCard>
              {/* Here whether or not an AI is seated. It used to appear with the
                  first one, which moved the settings under whatever the host was
                  reaching for at the time. */}
              <SettingsCard wide>
                {host ? (
                  <Toggle
                    checked={settings.llmBuzzMode === 'immediate'}
                    onChange={(next) => patch({ llmBuzzMode: next ? 'immediate' : 'human-like' })}
                    label={BUZZ_MODE_LABEL}
                  />
                ) : (
                  <ReadOnlyRow
                    label={BUZZ_MODE_LABEL}
                    value={settings.llmBuzzMode === 'immediate' ? 'on' : 'off'}
                  />
                )}
              </SettingsCard>
              </SettingsGroup>
            </div>
          ) : null}

          {/* What this room's AI seats will cost it, for the plan it is
              actually on.

              **The total is on the button rather than behind it.** Isaac asked
              for a predicted cost button, and a button whose whole job is to
              reveal one number is a number one press further away than it needs
              to be. What the press is for is the breakdown: which seat is
              spending it, which is the thing you cannot work out from a total.

              Only when a model is seated. A room of people and scripted bots
              spends nothing, and a row saying so is a row about nothing. */}
          {aiSeats.length > 0 ? (
            <div
              data-testid="ai-cost"
              className="rounded-card border border-line bg-surface transition-colors duration-200 ease-out"
            >
              <button
                type="button"
                aria-expanded={costOpen}
                onClick={() => setCostOpen(!costOpen)}
                className="flex min-h-11 w-full cursor-pointer items-center gap-2 px-3.5 text-left text-[14px] text-ink-dim transition-colors duration-150 ease-out hover:text-ink"
              >
                <Icon icon={BotIcon} size={16} />
                <span>Estimated AI cost</span>
                {/* The unit is spelt out for a screen reader for the same reason
                    it is in the model picker: a bare "4.8¢" read aloud is a
                    number with nothing to say what it counts. */}
                <span
                  aria-label={anySeatPriced ? seatCostLabel(totalCostUsd) : undefined}
                  className="ml-auto font-mono text-[13px] font-semibold text-ink tabular"
                >
                  {anySeatPriced ? formatSeatCost(totalCostUsd) : '—'}
                </span>
              </button>

              {costOpen ? (
                <div
                  data-testid="ai-cost-breakdown"
                  className="flex flex-col gap-1.5 border-t border-line px-3.5 py-2.5"
                >
                  {seatCosts.map((seat) => (
                    <div
                      key={seat.player.id}
                      data-cost-seat={seat.player.id}
                      className="flex items-baseline gap-2 text-[13px]"
                    >
                      {/* Both halves may shrink, and neither is `shrink-0`. A
                          24-character name (the display-name ceiling) beside a
                          21-character model label is wider than this column on
                          a phone, and a name that refuses to shrink pushes the
                          price off the end of the row instead of truncating. */}
                      <span className="min-w-0 truncate font-semibold text-ink">
                        {seat.player.displayName}
                      </span>
                      {/* The model under the name, because two seats can be one
                          model and the name alone would not say which row is
                          which. A seat this catalogue cannot describe still
                          gets a row: it is playing, whatever the price column
                          can say about it. */}
                      <span className="min-w-0 truncate text-ink-dim">
                        {seat.model?.label ?? modelName(seat.player.botModel ?? '', null)}
                      </span>
                      <span
                        aria-label={seat.usd === null ? 'no figure for this one' : seatCostLabel(seat.usd)}
                        className="ml-auto shrink-0 font-mono text-[12px] text-ink-dim tabular"
                      >
                        {seat.usd === null ? '—' : formatSeatCost(seat.usd)}
                      </span>
                    </div>
                  ))}
                  {/* **Said plainly, and it is not a hedge for its own sake.**
                      The token counts underneath this are a bench measurement
                      good to a few hundred tokens (CLAUDE.md), a model's spend
                      moves with the board, and two entries in the catalogue
                      were found to be wrong by 3x and 15x the day before this
                      was written, because a model changed under an unversioned
                      id. What the number is honest at is the resolution it is
                      shown at, which is whole cents. */}
                  <p className="pt-1 text-[12px] text-ink-muted">
                    {plan.length === 1 ? 'One round' : `${plan.length} rounds`}, at what{' '}
                    {seatCosts.length === 1 ? 'this model usually spends' : 'these models usually spend'}
                    . The real bill moves with the boards.
                  </p>
                </div>
              ) : null}
            </div>
          ) : null}

          {/* An AI opponent has to fetch its answer inside the clock, so a short
              round is the one setting that can stop it playing at all. Last in
              the column and grown into place rather than mounted, because the
              gesture that triggers it is a drag on a slider a few inches above:
              a paragraph appearing in one frame moves everything under the
              hand that is still holding the knob.

              **It names the model and the number it wants**, because "an
              opponent is slow" is a sentence a host can do nothing with. It was
              a flat "under 30s" rule written before anything had measured how
              long these models take, which is both too quiet — GPT-OSS 120B
              averages 44 seconds and a 30s room was told nothing at all — and
              too loud, since it fired for Haiku, which answers in about a
              second and a half.

              **It stops at saying so.** No seat is refused, no slider is
              clamped and nothing is disabled: Isaac's call is that a slow model
              is fine as long as the room is told, and the whole point of GPT-OSS
              120B being in the catalogue is that somebody may want to play it
              and give it the clock it needs. */}
          <Reveal open={slowModels.length > 0}>
            {slowModels.length > 0 ? (
              <div
                data-testid="llm-clock-warning"
                className="flex items-start gap-2 rounded-card border border-accent bg-accent/15 px-3.5 py-3 text-[13px] text-accent"
              >
                {/* A single wash on the page's own background, not a tint inside
                    a tinted card: `bg-accent/15` with `text-accent` is the
                    status panel the palette test measures, and nesting it in
                    something already tinted is the composite that falls off the
                    bottom of what the palette was solved for (CLAUDE.md). */}
                <Icon icon={AlertIcon} size={16} className="mt-0.5" />
                <div className="flex min-w-0 flex-col gap-1.5">
                  {slowModels.map((slow) => (
                    <p key={slow.model.id} data-slow-model={slow.model.id}>
                      {/* "usually" and "about" are both doing work. The figure
                          under them is a bench AVERAGE over six boards, so half
                          of real answers are slower than it, and a sentence that
                          reads as a deadline would be promising something
                          nobody has measured. The suggestion is the average
                          with headroom on it — see `suggestedClockSeconds`. */}
                      {slow.model.label} usually takes {slow.spoken} to answer.{' '}
                      {listNames(slow.rounds)} {slow.rounds.length === 1 ? 'is' : 'are'} shorter than
                      that, and {slow.wants}s would cover it.
                    </p>
                  ))}
                </div>
              </div>
            ) : null}
          </Reveal>
        </section>
      </div>

      {/* --- footer ---
          Sticky, so the two controls that end this screen are reachable without
          scrolling past the settings on a phone.

          **It reports its own height, and that is not decoration.** Sitting on
          the bottom of the screen means the bottom of the screen is not where
          anything else may open, and the host row menu was measuring itself
          against a band that did not know this bar existed. `actionBarRef` is
          the one measurement; see `lobby/actionBar.ts` for why it is measured
          rather than written down. */}
      <div
        ref={actionBarRef}
        data-testid="lobby-action-bar"
        className="sticky bottom-0 z-20 mt-auto border-t border-line bg-bg"
      >
        <div className="mx-auto flex w-full max-w-[1180px] flex-wrap items-center gap-2.5 px-4 py-3 lg:gap-3 lg:px-7 lg:py-4">
          {host ? (
            <Button
              size="lg"
              // Never gated on readiness. It never was — `startGame` does not
              // read `readyIds` — and a disabled Start beside a second button
              // that dispatched the identical action was two names for one
              // thing.
              disabled={seated.length === 0}
              onClick={() => send({ type: 'start-game', actionId: crypto.randomUUID() })}
              className="lg:min-w-[220px]"
            >
              Start game
            </Button>
          ) : null}

          {youAreSeated ? (
            <Button
              size="lg"
              // Unmistakable, rather than a border tint and a 14px icon swap:
              // off is the neutral outline every other control uses, on is the
              // room's green, filled and ringed, with the tick filled in.
              variant={youReady ? 'good' : 'outline'}
              aria-pressed={youReady}
              // The fill and the ring are inline rather than utilities because
              // the variant already sets a background and a border colour, and
              // which of two same-property utilities wins is decided by
              // stylesheet order rather than by the order they are written in.
              // `good/15` over a surface is the one tint the palette test
              // measures (`bg-good/15 text-good`), so this is that tint exactly.
              style={
                youReady
                  ? {
                      backgroundColor: 'color-mix(in oklab, var(--c-good) 15%, transparent)',
                      borderColor: 'var(--c-good)',
                      boxShadow: 'inset 0 0 0 2px var(--c-good)',
                    }
                  : undefined
              }
              onClick={() => send({ type: youReady ? 'unready' : 'ready-up' })}
            >
              {/* The slot has to be filled in both states, not merely reserved.
                  A ring that fills in costs the same width as the tick and says
                  what pressing the button will do. */}
              <Icon icon={youReady ? CheckIcon : CircleIcon} size={14} />
              <span>Ready</span>
            </Button>
          ) : null}

          {/* One slot for both sentences, wide enough for the longer of them, so
              the countdown starting does not move the buttons beside it. */}
          <span
            role="status"
            data-testid="ready-status"
            className={`min-w-[15ch] font-mono text-[13px] leading-[1.4] tabular ${
              autoStartArmed ? 'text-good' : 'text-ink-dim'
            }`}
          >
            {autoStartArmed ? (
              <AutoStart onFire={fireStart} />
            ) : everyoneReady ? (
              // Unanimously ready and going nowhere, which needs saying: the
              // count alone would read "6 of 6 ready" beside a game that never
              // starts. Bots are ready the moment they sit down, so this is
              // every bot-only room rather than an edge of one.
              host ? (
                'Bots only. Press Start game.'
              ) : (
                'Bots only. The host starts this one.'
              )
            ) : (
              `${readyCount} of ${seated.length} ready`
            )}
          </span>

          {/* Beside the button that starts it, because that is the last moment
              anybody can change their mind about it. Read from the room rather
              than from `settings.hostRulings`: the server decides what counts as
              recorded, and one derivation is what keeps this from disagreeing
              with the write. */}
          {state.unranked ? (
            <span
              data-testid="unranked"
              title="Host rulings are on. This game gets a page, and counts towards nobody's stats."
              // `accent` rather than `ink-dim`: this used to read `not recorded`
              // in the same grey as the ready count beside it, which is the
              // colour of a label rather than of a consequence. It is a warning
              // now because it is one — the setting is a lobby control and what
              // it costs lands forty minutes later.
              className="rounded-[5px] bg-accent/15 px-2 py-[5px] font-mono text-[10px] font-semibold tracking-[0.08em] text-accent uppercase"
            >
              unranked
            </span>
          ) : null}
        </div>
      </div>

      {safetyOpen && host ? (
        <SafetyPanel
          settings={settings}
          onPatch={patch}
          onClose={() => setSafetyOpen(false)}
        />
      ) : null}

      {editing !== null && you !== null ? (
        <EditPlayerSheet
          currentName={you.displayName}
          // Null in a room past `COLORED_SEATS`, where nobody has a colour and
          // the sheet says so instead of drawing a grid of them.
          currentColor={you.color}
          colorEditable={permitted}
          // Every other colour in the room, so nobody picks one that is already
          // somebody else. Nulls are dropped rather than counted as a colour.
          takenColors={state.players
            .filter((player) => player.id !== you.id)
            .map((player) => player.color)
            .filter((color): color is string => color !== null)}
          colorIsSecondary={teamed && you.teamId !== null}
          // A desktop keyboard costs nothing to focus into. A phone's covers
          // half the sheet, so only the pencil — which is the one target that
          // means "the name" — asks for it.
          autoFocusName={isDesktop || editing === 'name'}
          onPickColor={pickColor}
          onDone={(name) => {
            setEditing(null);
            if (name !== null) renameTo(name);
          }}
        />
      ) : null}

      {/* The round that is following the finger. In a portal so no scroll
          container or transformed ancestor can clip it. */}
      {drag !== null &&
        plan[drag.from] !== undefined &&
        createPortal(
          <div
            aria-hidden="true"
            className="pointer-events-none fixed z-50"
            style={{
              left: drag.x,
              top: drag.y,
              transform: 'translate(-50%, -50%) scale(1.12) rotate(-3deg)',
              filter: 'drop-shadow(0 10px 18px var(--c-scrim))',
            }}
          >
            {/* The number it will be, not the number it was: the position is the
                thing the host is trying to choose. */}
            <RoundTypeChip type={plan[drag.from]} position={drag.to + 1} />
          </div>,
          document.body,
        )}

      {/* The seat that is following the finger, portalled for the same reason:
          a row is carried right out of the column it came from, across whatever
          scroll container or transformed ancestor happens to be in the way, and
          it follows the pointer rather than the row — so unlike the host's row
          menu it has nothing to stay anchored to.

          **In the colour of where it is going, not where it came from.** Teams
          own colour outright, so a row on its way into Red is already Red — the
          gesture is the moment the player is deciding that, and the chip is the
          only thing on screen that can say it. Over nothing, it wears what it
          wears in the list. */}
      {playerDrag !== null &&
        draggedPlayer !== null &&
        createPortal(
          <div
            aria-hidden="true"
            data-testid="player-drag-ghost"
            data-ghost-color={colorInZone(draggedPlayer, playerDrag.zone) ?? undefined}
            className="pointer-events-none fixed z-50 w-[260px] max-w-[72vw]"
            style={{
              left: playerDrag.x,
              top: playerDrag.y,
              transform: 'translate(-50%, -50%) scale(1.03) rotate(-2deg)',
              filter: 'drop-shadow(0 10px 18px var(--c-scrim))',
            }}
          >
            <div className="flex items-center rounded-card border border-line-strong bg-surface px-2.5 py-1.5">
              <PlayerChip
                player={draggedPlayer}
                color={colorInZone(draggedPlayer, playerDrag.zone)}
                showHostCrown={draggedPlayer.isHost}
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
